import type { DirectorControl } from '../../../workspace/AgentChat'
import { EditSpecTimelineEditor, type EditSpecTimelineEditorHandle } from './EditSpecTimelineEditor'
import { stepTimelineFrame, LEGACY_TIMELINE_FPS } from './timelineNavigation'
import { editSpecSourceKey, projectEditSpecTimeline } from './editSpecTimeline'
import { useEditSpecSourceDurations } from './useEditSpecSourceDurations'
import { ydocSafeCloneNodeData } from '../../../../utils/ydocNodeData'
/**
 * Timeline Editor Modal (canvas-v2)
 *
 * Full-screen portal with 4-panel CSS grid layout:
 * - ClipPanel (left), VideoPreviewPlayer (center), AdjustPanel (right)
 * - TimelineEditor (bottom)
 *
 * Differences from V1:
 * - 4-panel grid layout (not vertical stack)
 * - Uses executeNode (fire-and-forget via Orchestrator)
 * - Render config includes speed/track_index per clip
 * - Ctrl/Cmd+C/V copy/paste shortcuts
 * - useClipPreloader for blob-based video/audio preloading
 * - useAudioPool for stable multi-track audio playback
 */

import { createPortal, flushSync } from 'react-dom'
import { lazy, Suspense, useState, useCallback, useEffect, useMemo, useRef } from 'react'
import { useNodes } from 'reactflow'
import { ArrowLeft, MessageSquare, Maximize, FolderOpen, Files, SlidersHorizontal } from 'lucide-react'
import { TimelineEditor } from './TimelineEditor'
import { VideoPreviewPlayer, type TimelineVideoPreviewPlayerHandle } from './VideoPreviewPlayer'
import { useLegacyTextTracks } from './useLegacyTextTracks'
import { TimelineShortcutLegend } from './TimelineShortcutLegend'
import { LegacyTextPreview } from './LegacyTextPreview'
import { applyTextDraft, type TextDraft } from './textTrack'
import { dreamwriteEditSpecDurationSeconds } from './dreamvibeAdapter'
import { AllMediaPanel } from './AllMediaPanel'
import type { CanvasAssetCandidate } from '../../nodes/shared/mentionUtils'
import { ClipPanel } from './ClipPanel'
import { AdjustPanel } from './AdjustPanel'
import type { SpeedPoint } from './speedCurve'
import { useTimelineState } from './useTimelineState'
import { summarizeSelection } from './timelineSelection'
import { useTimelinePlayback } from './useTimelinePlayback'
import { useTimelineTimeEdit } from './useTimelineTimeEdit'
import { useClipPreloader } from './useClipPreloader'
import { useAudioPool } from './useAudioPool'
import {
  buildSelectiveAudioFillClips,
  buildInitialTimelineAudioClips,
  computeTimelineDuration,
  buildTimelineRenderConfig,
  parseTimelineRenderConfig,
  computeConnectedSourceKey,
  computeMaterializableSourceIds,
  updatePendingConnectedSourceIds,
  timelineClipDurationOrDefault,
  timelineConfigTraceHash,
  timelineStringTraceHash,
  coerceTimelineDurationHint,
  isSymbolicTimelineSourceRef,
  applyRealDurationsToTimeline,
  packMainTrack,
  appendMainTrackReferences,
  isPristineTimelineRenderConfig,
  isVideoExportDurationTooShort,
  hasExtractedNativeAudioForVideo,
} from './timelineUtils'
import {
  bindTimelineExitFlush,
  shouldFlushTimelineOnExit,
  type TimelineExitFlushOrigin,
} from './timelinePersistence'
import {
  executeNode,
  cancelNodeExecution,
  getVideoEditorAudioActions,
  runVideoEditorAudioAction,
  type CharacterVoiceCatalogueEntry,
} from '../../../../api/client'
import type {
  VideoEditorAudioActionsResponse,
  VideoEditorAudioActionRunResponse,
  VideoEditorDerivedAudioClip,
} from '../../../../api/responseDecoders'
import { useProjectId } from '../../../../contexts/ProjectIdContext'
import { trackClipAddedToTimeline } from '../../../../analytics/events'
import { useActiveCanvasId } from '../../../../contexts/ActiveCanvasIdContext'
import { useCanvasOptional } from '../../CanvasContext'
import { useBlockingOverlay } from '../../../../hooks/canvasShortcutGuard'
import { createDebug, createNadDebug } from '../../../../utils/debugLog'
import { useTranslation } from 'react-i18next'
import { toast } from 'sonner'
import { fetchMediaBlob } from '../../../../api/mediaFetch'
import { downloadTimelineAudioClip } from './downloadTimelineAudioClip'
import { beginAssetDownload, currentWorkspaceViewMode } from '../../../../analytics/assetDownloadAnalytics'
import { trackGenerationCancelled, trackTimelineEdit } from '../../../../analytics/interactionAnalytics'

// Module-scope debug logger for the cancel trace. Gated by
// VITE_NAD_DEBUG (enables ``dw:*``) — zero overhead in production.
// Feature tag reuses ``video-editor`` from docs/FEATURE_TAGS.md.
const cancelDebug = createDebug('dw:video-editor:cancel')
import { emitTimelineNadEvent } from '../../audio-edit/nadTimelineLog'
import {
  ExportConfirmSheet,
  buildDefaultExportName,
} from '../../nodes/VideoEditorNode/ExportConfirmSheet'
import { type ReactFlowCanvasNodeData } from '../../../../hooks/useYCanvas'
import {
  type TimelineClip,
  type TimelineAudioClip,
  type TimelineClipOperationStateMap,
  type TimelineRenderConfig,
  type TimelineState,
} from './types'
import type { EditSpecDict, EditSpecSourceRefDict } from '../../../../generated/nodeDataTypes'
import {
  audioKindForLegacyRole,
  type LegacyTimelineAudioRole,
  type TimelineAudioKind,
} from './timelineAudioKind'

const ScopedAgentChat = lazy(async () => {
  const module = await import('../../../workspace/AgentChat')
  return { default: module.AgentChat }
})

function timelineAudioClipFromDerived(clip: VideoEditorDerivedAudioClip): TimelineAudioClip {
  const originalDuration = Math.max(0.01, clip.original_duration)
  const trimStart = Math.max(0, clip.start_time)
  const sourceEnd = Math.max(trimStart, Math.min(originalDuration, clip.end_time))
  const trimEnd = Math.max(0, originalDuration - sourceEnd)
  const speed = clip.speed > 0 ? clip.speed : 1
  return {
    id: clip.id,
    sourceNodeId: clip.source_node_id,
    sourcePath: clip.source_path,
    label: clip.label ?? undefined,
    audioKind: clip.audio_kind ?? audioKindForLegacyRole(clip.legacy_role ?? undefined) ?? undefined,
    originalDuration,
    trimStart,
    trimEnd,
    effectiveDuration: Math.max(0.01, (sourceEnd - trimStart) / speed),
    volume: clip.volume,
    muted: clip.muted,
    startOffset: clip.start_offset,
    plannedEndOffset: clip.planned_end_offset ?? undefined,
    role: clip.legacy_role ?? undefined,
    speed,
    trackIndex: clip.track_index,
    splitGroupId: clip.split_group_id ?? undefined,
    peakDbfs: clip.peak_dbfs ?? undefined,
  }
}

const DreamVibePreview = lazy(async () => {
  const module = await import('./DreamVibePreview')
  return { default: module.DreamVibePreview }
})

interface TimelineEditorModalProps {
  /** VideoEditorNode ID */
  nodeId: string
  /** Connected video clips */
  videoClips: Array<{ id: string; path: string; name: string; duration?: number }>
  /** Connected audio clips */
  audioClips: Array<{
    id: string
    path: string
    name: string
    duration?: number
    audioKind?: TimelineAudioKind
    role?: LegacyTimelineAudioRole
  }>
  /** Canonical timeline config from the Video Editor node's current YDoc data. */
  timelineConfig?: TimelineRenderConfig
  /** Graph-connected video source IDs, including media that has not hydrated yet. */
  connectedVideoSourceIds: string[]
  /** Graph-connected audio source IDs, including media that has not hydrated yet. */
  connectedAudioSourceIds: string[]
  /** Historical variants keyed by canonical source identity (`node.port@index`). */
  dreamVibeHistoricalSources?: Readonly<Record<string, string>>
  /** Node execution status mirrored from YDoc/React Flow */
  renderStarting?: boolean
  nodeStatus?: string
  /** Node progress message mirrored from YDoc/React Flow */
  nodeProgressMessage?: string | null
  /** Canonical DreamVibe edit, used only in the explicit primary compatibility state. */
  editSpec?: EditSpecDict | null
  editSpecCompatibilityState?: string | null
  renderedSpecHash?: string | null
  editSpecRevision?: number | null
  dreamVibePreviewEnabled: boolean
  dreamVibeAuthoringEnabled: boolean
  dreamVibeRenderEnabled: boolean
  /** Current connected-source failure/pending reason. Blocks export, not editing. */
  sourceBlockReason?: string
  /** Close callback */
  onClose: () => void
  /**
   * RFC-0651 #6: open the canvas-level reference selector that the
   * VideoEditorNode toolbar already uses. Plumbed into the ClipPanel as
   * a "+ Add References" button. When undefined, the button is hidden.
   */
  mediaImportBusy?: boolean
  importedMedia?: CanvasAssetCandidate[]
  onImportMedia?: (asset: CanvasAssetCandidate) => void
  onOpenReferenceSelector?: () => void
}

type SelectedAudioActionTarget = {
  targetId: string
  sourceClipId: string
  sourceClipType: 'video' | 'audio'
}

type AudioActionId = 'isolate_audio' | 'change_voice' | 'split_dialogue'

type RunningAudioAction = SelectedAudioActionTarget & {
  actionId: AudioActionId
}

type PendingAudioAction = SelectedAudioActionTarget & {
  actionId: 'isolate_audio'
  jobId: string
}

type AudioActionErrorState = {
  sourceClipId: string
  message: string
}

const timelineLog = createNadDebug('dw:timeline-playback:render-contract')
const selectiveAudioLog = createNadDebug('dw:selective-master-track-audio:gap-fill')
const exportFlowLog = createNadDebug('dw:video-editor:export-flow')
const timelineQaLog = createNadDebug('dw:video-editor:timeline-qa')
const TIMELINE_SOURCE_NAMESPACE = 'dw:video-editor:timeline-source-of-truth'
const timelineSourceLog = createNadDebug(TIMELINE_SOURCE_NAMESPACE)
const audioDownloadLog = createNadDebug('dw:video-editor:audio-download')
const audioEditorActionLog = createNadDebug('dw:video-editor:timeline-audio-actions')

type TimelineSourceEvent = { event: string } & Record<string, string | number | boolean | null | object | undefined>

function historicalSourceIdentities(editSpec: EditSpecDict | null): ReadonlySet<string> {
  const identities = new Set<string>()
  const addSource = (source: EditSpecSourceRefDict | undefined, includeCurrent = true) => {
    if (!source) return
    if (source.history_index === undefined) {
      if (includeCurrent) identities.add(`${source.node_id}.${source.output_port}`)
      return
    }
    identities.add(`${source.node_id}.${source.output_port}@${source.history_index}`)
  }
  for (const clip of editSpec?.clips ?? []) addSource(clip.source)
  for (const overlay of editSpec?.overlay_clips ?? []) addSource(overlay.source)
  for (const overlay of editSpec?.overlay_clips ?? []) {
    for (const source of overlay.graphic_sources ?? []) addSource(source)
  }
  for (const track of editSpec?.audio?.tracks ?? []) addSource(track.source)
  return identities
}

function emitTimelineSourceEvent(data: TimelineSourceEvent): void {
  timelineSourceLog(data)
  emitTimelineNadEvent(TIMELINE_SOURCE_NAMESPACE, data)
}

// RFC-0651 #5 panel-divider clamps (module-level so they can be referenced
// from `useCallback` without becoming a stale-closure trap).
const PANEL_CLAMPS: Record<'left' | 'right' | 'timeline', { min: number; max: number }> = {
  left: { min: 280, max: 480 },
  right: { min: 200, max: 400 },
  timeline: { min: 200, max: 500 },
}

function timelineConfigFingerprint(config: TimelineRenderConfig | null | undefined): string {
  return config ? JSON.stringify(config) : ''
}

function timelineEditablePlacementFingerprint(state: TimelineState): string {
  return JSON.stringify({
    clips: state.clips.map(clip => ({
      id: clip.id,
      sourceNodeId: clip.sourceNodeId,
      sourcePath: clip.sourcePath,
      sourceRef: clip.sourceRef,
      order: clip.order,
      startOffset: clip.startOffset,
      trackIndex: clip.trackIndex,
      trimStart: clip.trimStart,
      trimEnd: clip.trimEnd,
      speed: clip.speed,
      volume: clip.volume,
      muted: clip.muted,
      fadeInS: clip.fadeInS,
      fadeOutS: clip.fadeOutS,
      audioFadeInS: clip.audioFadeInS,
      audioFadeOutS: clip.audioFadeOutS,
    })),
    audioClips: state.audioClips.map(clip => ({
      id: clip.id,
      sourceNodeId: clip.sourceNodeId,
      sourcePath: clip.sourcePath,
      sourceRef: clip.sourceRef,
      startOffset: clip.startOffset,
      plannedEndOffset: clip.plannedEndOffset,
      trackIndex: clip.trackIndex,
      trimStart: clip.trimStart,
      trimEnd: clip.trimEnd,
      speed: clip.speed,
      volume: clip.volume,
      muted: clip.muted,
      audioFadeInS: clip.audioFadeInS,
      audioFadeOutS: clip.audioFadeOutS,
    })),
    videoTrackCount: state.videoTrackCount,
    audioTrackCount: state.audioTrackCount,
    mutedVideoTracks: Array.from(state.mutedVideoTracks).sort((a, b) => a - b),
    mutedAudioTracks: Array.from(state.mutedAudioTracks).sort((a, b) => a - b),
    magnetEnabled: state.magnetEnabled,
    textOverlays: state.textOverlays, textFormat: state.textFormat,
  })
}

function classifyTimelineSourceRef(
  sourcePath: string | null | undefined,
): 'missing' | 'symbolic' | 'voice-acting-literal' | 'literal' {
  if (!sourcePath || sourcePath.trim() === '') return 'missing'
  const trimmed = sourcePath.trim()
  // Single source of truth for the symbolic-ref predicate lives in
  // timelineUtils so build/parse/reconstruct stay aligned (RFC-1035).
  if (isSymbolicTimelineSourceRef(trimmed)) return 'symbolic'
  if (trimmed.includes('assets/audio/voice_acting/')) return 'voice-acting-literal'
  return 'literal'
}

function summarizeTimelineSourceRefs(config: TimelineRenderConfig): Record<string, number> {
  const summary: Record<string, number> = {
    videoSymbolic: 0,
    videoLiteral: 0,
    videoVoiceActingLiteral: 0,
    videoMissing: 0,
    audioSymbolic: 0,
    audioLiteral: 0,
    audioVoiceActingLiteral: 0,
    audioMissing: 0,
  }

  for (const clip of config.timeline?.clips ?? []) {
    const kind = classifyTimelineSourceRef(clip.source_path)
    if (kind === 'symbolic') summary.videoSymbolic += 1
    else if (kind === 'voice-acting-literal') summary.videoVoiceActingLiteral += 1
    else if (kind === 'literal') summary.videoLiteral += 1
    else summary.videoMissing += 1
  }

  const audioTracks = config.timeline?.audio_tracks ?? (config.timeline?.audio ? [config.timeline.audio] : [])
  for (const track of audioTracks) {
    const kind = classifyTimelineSourceRef(track.source_path)
    if (kind === 'symbolic') summary.audioSymbolic += 1
    else if (kind === 'voice-acting-literal') summary.audioVoiceActingLiteral += 1
    else if (kind === 'literal') summary.audioLiteral += 1
    else summary.audioMissing += 1
  }

  return summary
}

export function TimelineEditorModal({
  nodeId,
  videoClips,
  audioClips: audioClipRefs,
  timelineConfig,
  connectedVideoSourceIds,
  connectedAudioSourceIds,
  dreamVibeHistoricalSources = {},
  renderStarting = false,
  nodeStatus = 'idle',
  nodeProgressMessage,
  editSpec,
  editSpecCompatibilityState,
  renderedSpecHash,
  editSpecRevision = 0,
  dreamVibePreviewEnabled,
  dreamVibeRenderEnabled,
  sourceBlockReason,
  onClose,
  onOpenReferenceSelector, importedMedia = [], onImportMedia, mediaImportBusy = false,
}: TimelineEditorModalProps) {
  const { t } = useTranslation('canvas')
  const projectId = useProjectId()
  const canvasId = useActiveCanvasId()
  const canvas = useCanvasOptional()

  // Suspend canvas keyboard shortcuts while this overlay is open (see canvasShortcutGuard).
  // The modal is mounted only while open (parent gates on isTimelineEditorOpen), so it
  // blocks unconditionally whenever rendered.
  useBlockingOverlay(true)

  const [isRendering, setIsRendering] = useState(false)
  const [renderProgress, setRenderProgress] = useState(0)
  const [renderMessage, setRenderMessage] = useState('')
  const [isCancelling, setIsCancelling] = useState(false)
  const [isExportSheetOpen, setIsExportSheetOpen] = useState(false)
  const [timelineLoaded, setTimelineLoaded] = useState(false)
  const timelineLoadedRef = useRef(timelineLoaded)
  timelineLoadedRef.current = timelineLoaded
  const [activeEditSpec, setActiveEditSpec] = useState<EditSpecDict | null>(() => editSpec ? ydocSafeCloneNodeData(editSpec) : null)
  const [activeEditSpecRevision, setActiveEditSpecRevision] = useState(editSpecRevision ?? 0)
  const activeEditSpecRevisionRef = useRef(editSpecRevision ?? 0)
  const incomingEditSpecRevisionRef = useRef(editSpecRevision ?? 0)
  incomingEditSpecRevisionRef.current = editSpecRevision ?? 0
  const [directorControl, setDirectorControl] = useState<DirectorControl | null>(null)
  const [editSpecSaving, setEditSpecSaving] = useState(false)
  const [stoppingDirector, setStoppingDirector] = useState(false)
  const [editSpecInspector, setEditSpecInspector] = useState<HTMLDivElement | null>(null)
  const [rightPanelMode, setRightPanelMode] = useState<'all' | 'imported' | 'config'>('config')
  useEffect(() => {
    if ((editSpecRevision ?? 0) < activeEditSpecRevisionRef.current) return
    activeEditSpecRevisionRef.current = editSpecRevision ?? 0
    setActiveEditSpec(editSpec ? ydocSafeCloneNodeData(editSpec) : null)
    setActiveEditSpecRevision(editSpecRevision ?? 0)
  }, [editSpec, editSpecRevision])
  const commitActiveEditSpec = useCallback((spec: EditSpecDict, revision: number) => {
    if (revision < Math.max(activeEditSpecRevisionRef.current, incomingEditSpecRevisionRef.current)) return
    activeEditSpecRevisionRef.current = revision
    setActiveEditSpec(spec)
    setActiveEditSpecRevision(revision)
  }, [])
  const handleStopDirector = useCallback(async () => {
    if (!directorControl || stoppingDirector) return
    setStoppingDirector(true)
    try {
      await directorControl.stop()
      setDirectorControl((current) => current && { ...current, active: false })
      timelineLog({ event: 'director-control-released', nodeId })
    } catch (cause) {
      toast.error(cause instanceof Error ? cause.message : String(cause))
    } finally {
      setStoppingDirector(false)
    }
  }, [directorControl, stoppingDirector, nodeId])
  const [exportSheetDefaultName, setExportSheetDefaultName] = useState(() =>
    buildDefaultExportName(),
  )
  const allCanvasNodes = useNodes<ReactFlowCanvasNodeData>()
  const existingLabels = useMemo(() => {
    const labels = new Set<string>()
    for (const node of allCanvasNodes) {
      if (node.id === nodeId) continue
      const rawLabel = node.data?.label
      const label = typeof rawLabel === 'string' && rawLabel ? rawLabel : node.id
      if (label) labels.add(label)
    }
    return labels
  }, [allCanvasNodes, nodeId])
  const isRenderActive =
    renderStarting || isRendering ||
    nodeStatus === 'running' ||
    nodeStatus === 'pending_execution' ||
    nodeStatus === 'pending_cost_confirmation'
  const directorActive = directorControl?.active ?? (canvas?.executionActivity?.ydocStatusLive === true && canvas.executionActivity.ydocDirectorRunning)
  const timelineLocked = isRenderActive || directorActive || stoppingDirector || editSpecSaving || mediaImportBusy
  const isEditSpecPrimary = editSpecCompatibilityState === 'editspec_primary'
  const [selectedTextKey, setSelectedTextKey] = useState<string | null>(null)
  // One draft slot serves both previews; `source` keeps a DreamVibe (EditSpec) drag from being projected
  // onto the legacy text document and vice versa (that threw "Unknown Text item" mid-render).
  const [textDraft, setTextDraft] = useState<TextDraft | null>(null)
  const textEditorRef = useRef<EditSpecTimelineEditorHandle>(null)
  const previewTextSpec = activeEditSpec && textDraft?.revision === activeEditSpecRevision ? applyTextDraft(activeEditSpec, textDraft, 'editspec') : activeEditSpec
  const useDreamVibePreview = dreamVibePreviewEnabled && isEditSpecPrimary && activeEditSpec !== null
  const dreamVibeRenderUnavailable = isEditSpecPrimary && !dreamVibeRenderEnabled
  const dreamVibePreviewUnavailable = isEditSpecPrimary && !useDreamVibePreview
  const referencedHistoricalSources = useMemo(
    () => historicalSourceIdentities(activeEditSpec),
    [activeEditSpec],
  )

  // Convert video clips to TimelineClip format
  const initialClips: TimelineClip[] = useMemo(() => {
    let nextStartOffset = 0
    return videoClips.map((clip, index) => {
      const duration = timelineClipDurationOrDefault(clip.duration)
      const timelineClip: TimelineClip = {
        id: clip.id,
        sourceNodeId: clip.id,
        sourcePath: clip.path,
        sourceRef: `${clip.id}.out_video`,
        originalDuration: duration,
        trimStart: 0,
        trimEnd: 0,
        effectiveDuration: duration,
        order: index,
        startOffset: nextStartOffset,
        trackIndex: 0,
        label: clip.name,
        speed: 1.0,
        volume: 1.0,
        muted: false,
      }
      nextStartOffset += duration
      return timelineClip
    })
  }, [videoClips])

  // Convert audio clips to TimelineAudioClip format
  const initialAudioClips: TimelineAudioClip[] = useMemo(
    () => buildInitialTimelineAudioClips(audioClipRefs),
    [audioClipRefs]
  )
  const connectedVideoSourceIdSet = useMemo(
    () => new Set(connectedVideoSourceIds),
    [connectedVideoSourceIds],
  )
  const connectedAudioSourceIdSet = useMemo(
    () => new Set(connectedAudioSourceIds),
    [connectedAudioSourceIds],
  )

  // Stable keys: only change when clip IDs or paths actually change
  const videoClipsKey = videoClips.map((c) => `${c.id}:${c.path}`).join('|')
  const audioFileKey = audioClipRefs.map((a) => `${a.id}:${a.path}`).join('|')
  const videoClipsRef = useRef(videoClips)
  videoClipsRef.current = videoClips
  const audioClipRefsRef = useRef(audioClipRefs)
  audioClipRefsRef.current = audioClipRefs

  // Timeline state management
  const {
    timelineState,
    editRevision,
    addClip,
    addAudioClips,
    applyDerivedAudioClips,
    setPlayhead: setLegacyPlayhead,
    setZoom,
    togglePlayPause,
    selectClip,
    toggleClipSelection,
    clearSelection,
    rippleDeleteSelected,
    multiDragOffset,
    reorderClip,
    trimClip,
    splitClipByDialogue,
    splitAudioClipByDialogue,
    trimClipPreview,
    setAudioVolume,
    setAudioVolumePreview,
    commitAudioVolume,
    toggleAudioMute,
    trimAudioClip,
    trimAudioClipPreview,
    setAudioOffset,
    setAudioTrackIndex,
    setClipOffset,
    setClipTrackIndex,
    setClipSpeed,
    setClipVisuals,
    replaceClipSource,
    setAudioFades,
    cutAtPlayhead,
    setTransition,
    setClipVolume,
    setClipVolumePreview,
    commitClipVolume,
    setClipFadeOut,
    setClipFadeIn,
    normalizeTimelineOrigin,
    closeAllGaps,
    toggleClipMute,
    setAudioSpeed,
    extractNativeVideoAudio,
    copyClip,
    pasteClip,
    deleteSelectedClip,
    splitSelectedClip,
    addVideoTrack,
    addAudioTrack,
    removeVideoTrack,
    removeAudioTrack,
    undo,
    redo,
    canUndo,
    canRedo,
    restoreState,
    refreshTimelineState,
    setTextOverlays,
    toggleTrackMute,
    setMagnetEnabled,
    magnetPackTrack0,
  } = useTimelineState(undefined, { projectId, nodeId })

  // Ref for accessing latest timelineState in async callbacks
  const timelineStateRef = useRef(timelineState)
  timelineStateRef.current = timelineState
  const getTimeEditClip = useCallback((id: string) => timelineStateRef.current.clips.find((clip) => clip.id === id), [])
  const { run: runTimeEdit, busy: timeEditBusy, error: timeEditError } = useTimelineTimeEdit(projectId, getTimeEditClip, replaceClipSource)
  const hasPlaybackClips = isEditSpecPrimary ? (activeEditSpec?.clips.length ?? 0) > 0 : timelineState.clips.length > 0 || (timelineState.textOverlays?.length ?? 0) > 0
  const clipsLength = timelineState.clips.length
  const audioClipsLength = timelineState.audioClips.length

  useEffect(() => {
    // Safety-net effect: broadened to handle ``idle``/``pending_execution``
    // alongside ``completed``/``error`` so the render overlay tears down
    // for every terminal-ish status. Previously this only matched
    // ``completed``/``error``; the orchestrator's legacy cancel restored
    // nodes to ``idle`` or kept ``completed``, which meant the overlay
    // never unmounted on cancel. The video_editor cancel branch in
    // orchestrator.cancel_node now writes ``error`` with "Cancelled by
    // user" — that hits the first arm. The ``idle``/``pending_execution``
    // arm also catches non-video_editor cancels and crash-recovery
    // reloads where the node ends up back in those states.
    if (
      nodeStatus === 'completed' ||
      nodeStatus === 'error' ||
      nodeStatus === 'idle' ||
      nodeStatus === 'pending_execution'
    ) {
      cancelDebug(
        'status_transition_terminal nodeId=%s status=%s',
        nodeId,
        nodeStatus,
      )
      setIsRendering(false)
      setRenderProgress(0)
      // Clear the stale progress text alongside progress%. If the
      // backend supplied a new message (e.g. an error string), keep
      // it; otherwise wipe the previous "Rendering..." line.
      if (nodeProgressMessage) {
        setRenderMessage(nodeProgressMessage)
      } else {
        setRenderMessage('')
      }
    } else if (nodeStatus === 'running') {
      setIsRendering(true)
      if (nodeProgressMessage) {
        setRenderMessage(nodeProgressMessage)
      }
    }
  }, [nodeId, nodeStatus, nodeProgressMessage])

  // Collect all unique source paths for preloading.
  // RFC-0631 (Issue 7): also include `timelineState.clips[*].sourcePath`
  // and `timelineState.audioClips[*].sourcePath` so freshly-inserted clips
  // (e.g. the WAV produced by Extract Audio) are preloaded even when they
  // have no upstream edge. Path-keyed Set dedupes against edge-derived
  // entries — no double download.
  const allSourcePaths = useMemo(() => {
    const paths = new Set<string>()
    for (const clip of videoClips) paths.add(clip.path)
    for (const clip of audioClipRefs) paths.add(clip.path)
    for (const clip of timelineState.clips) {
      if (clip.sourcePath && !isSymbolicTimelineSourceRef(clip.sourcePath)) paths.add(clip.sourcePath)
    }
    for (const clip of timelineState.audioClips) {
      if (clip.sourcePath && !isSymbolicTimelineSourceRef(clip.sourcePath)) paths.add(clip.sourcePath)
    }
    for (const identity of referencedHistoricalSources) {
      const path = dreamVibeHistoricalSources[identity]
      if (path) paths.add(path)
    }
    return Array.from(paths)
  }, [dreamVibeHistoricalSources, referencedHistoricalSources, videoClips, audioClipRefs, timelineState.clips, timelineState.audioClips])

  // Preload all clips as blobs
  const preloader = useClipPreloader({ projectId, sourcePaths: allSourcePaths })

  // Audio pool for stable multi-track playback
  const audioPool = useAudioPool({
    audioClips: timelineState.audioClips,
    playheadPosition: timelineState.playheadPosition,
    isPlaying: timelineState.isPlaying && !isEditSpecPrimary,
    preloader,
    mutedAudioTracks: timelineState.mutedAudioTracks,
  })
  const videoPreviewRef = useRef<TimelineVideoPreviewPlayerHandle>(null)
  const resumeAudioContexts = useCallback(() => {
    videoPreviewRef.current?.resumeAudioContexts()
    audioPool.resumeAudioContexts()
  }, [audioPool])

  // Download the SOURCE audio file for a timeline audio clip (extracted,
  // uploaded, or split). The behavior lives in the injectable
  // `downloadTimelineAudioClip` helper (unit-tested); here we wire the real
  // deps. `getBlobUrl` is destructured (stable identity) so this callback — and
  // thus the memoized AdjustPanel — is not recreated every render by the
  // preloader wrapper object.
  const { getBlobUrl, revision: preloadRevision } = preloader
  const dreamVibeSourceRevisions = useMemo(() => {
    const sources: Record<string, string> = {}
    for (const identity of referencedHistoricalSources) {
      const currentPort = identity.endsWith('.out_video')
        ? 'out_video'
        : identity.endsWith('.out_audio')
          ? 'out_audio'
          : null
      const sourceId = currentPort ? identity.slice(0, -(currentPort.length + 1)) : ''
      const path = dreamVibeHistoricalSources[identity]
        ?? (currentPort === 'out_video'
          ? videoClips.find((clip) => clip.id === sourceId)?.path
          : currentPort === 'out_audio'
            ? audioClipRefs.find((clip) => clip.id === sourceId)?.path
            : undefined)
      if (path) sources[identity] = path
    }
    return sources
  }, [audioClipRefs, dreamVibeHistoricalSources, referencedHistoricalSources, videoClips])
  const dreamVibeSources = useMemo(() => {
    void preloadRevision
    const sources: Record<string, string> = {}
    for (const identity of referencedHistoricalSources) {
      const path = dreamVibeHistoricalSources[identity]
      if (!path) continue
      sources[identity] = getBlobUrl(path) ?? path
    }
    for (const clip of videoClips) {
      sources[`${clip.id}.out_video`] = getBlobUrl(clip.path) ?? clip.path
    }
    for (const clip of audioClipRefs) {
      sources[`${clip.id}.out_audio`] = getBlobUrl(clip.path) ?? clip.path
    }
    return sources
  }, [
    audioClipRefs,
    dreamVibeHistoricalSources,
    getBlobUrl,
    preloadRevision,
    referencedHistoricalSources,
    videoClips,
  ])
  const editSpecVideoBlobs = useMemo(() => {
    void preloadRevision
    const blobs: Record<string, string> = {}
    if (isEditSpecPrimary) for (const clip of activeEditSpec?.clips ?? []) {
      if (clip.source.media_type !== 'video') continue
      const identity = editSpecSourceKey(clip.source)
      const path = dreamVibeSourceRevisions[identity]
      const blob = path ? getBlobUrl(path) : null
      if (blob) blobs[identity] = blob
    }
    return blobs
  }, [activeEditSpec, isEditSpecPrimary, dreamVibeSourceRevisions, getBlobUrl, preloadRevision])
  const editSpecSourceMetadata = useEditSpecSourceDurations(editSpecVideoBlobs)
  const playbackDuration = useMemo(
    () => isEditSpecPrimary && activeEditSpec
      ? dreamwriteEditSpecDurationSeconds(activeEditSpec)
      : timelineState.totalDuration,
    [activeEditSpec, isEditSpecPrimary, timelineState.totalDuration],
  )
  // The canonical EditSpec may have no legacy clips, and Text can extend past footage.
  const setPlayhead = useCallback((position: number) => {
    setLegacyPlayhead(position, isEditSpecPrimary ? playbackDuration : undefined)
  }, [setLegacyPlayhead, isEditSpecPrimary, playbackDuration])
  const playbackTimelineState = useMemo(
    () => ({ ...timelineState, totalDuration: playbackDuration }),
    [playbackDuration, timelineState],
  )
  const exportDurationTooShort = isVideoExportDurationTooShort(playbackDuration)
  const minimumExportDurationMessage = t('videoEditorNode.minimumExportDuration')
  useEffect(() => {
    if (!isEditSpecPrimary) return
    const current = timelineStateRef.current
    if (Math.abs(current.totalDuration - playbackDuration) < 0.001) return
    refreshTimelineState({ ...current, totalDuration: playbackDuration })
  }, [isEditSpecPrimary, playbackDuration, refreshTimelineState])
  const handleDownloadAudioClip = useCallback(
    (clip: TimelineAudioClip) => {
      void downloadTimelineAudioClip(clip, {
        projectId,
        getBlobUrl,
        fetchMedia: fetchMediaBlob,
        log: (fields) => audioDownloadLog({ nodeId, ...fields }),
        toastError: (message) => toast.error(message),
        // RFC-1577 asset_download_action. The clip's source node is not known
        // here, so no node_id; the timeline has no dedicated entry point.
        beginAttempt: projectId
          ? (filename) => beginAssetDownload({
              projectId,
              entryPoint: 'other',
              target: 'node_output',
              viewMode: currentWorkspaceViewMode(),
              mediaType: 'audio',
              filename,
            })
          : undefined,
        messages: {
          noProject: t('nodeActions.downloadFailedNoProject'),
          unknownError: t('nodeActions.unknownError'),
          downloadFailed: (message) => t('nodeActions.downloadFailed', { message }),
        },
      })
    },
    [getBlobUrl, projectId, nodeId, t],
  )

  // Load actual video and audio durations from preloader blobs
  const loadedDurationsRef = useRef<Map<string, number>>(new Map())
  const [durationsLoaded, setDurationsLoaded] = useState(false)

  // T279076566: signature of the edge-connected source-id set at the previous
  // merge run. Used to decide whether the undo history must RESET: only a genuine
  // edge topology change (a source added/removed at the edge level) resets it.
  // A user-owned timeline divergence (e.g. the user deleted a clip whose source
  // edge is still connected) followed by an `allNodes` status-mirror churn must
  // NOT reset — otherwise background churn silently kills Undo.
  const prevEdgeSourceKeyRef = useRef<string | null>(null)
  const prevConnectedVideoSourceIdsRef = useRef<Set<string>>(new Set())
  const prevConnectedAudioSourceIdsRef = useRef<Set<string>>(new Set())
  const pendingConnectedVideoSourceIdsRef = useRef<Set<string>>(new Set())
  const pendingConnectedAudioSourceIdsRef = useRef<Set<string>>(new Set())

  const autosaveTimerRef = useRef<ReturnType<typeof setTimeout> | null>(null)

  // T279076566 (Finding A): whether the timeline has performed its one-time
  // initial load/restore. The fresh-init branch of the merge effect must run
  // ONLY once — selecting it by `currentClipsLength === 0` meant that deleting
  // the last clip and then hitting a background `allNodes`/status churn was
  // mistaken for a fresh load, wiping undo and resurrecting the clips.
  const hasInitializedRef = useRef(false)

  // Reset when source clips/audio change so durations are re-fetched
  const prevKeysRef = useRef(`${videoClipsKey}|${audioFileKey}`)
  useEffect(() => {
    const currentKeys = `${videoClipsKey}|${audioFileKey}`
    if (prevKeysRef.current !== currentKeys) {
      prevKeysRef.current = currentKeys
      loadedDurationsRef.current = new Map()
      setDurationsLoaded(false)
    }
  }, [videoClipsKey, audioFileKey])

  // Probe durations using preloader blob URLs (no redundant downloads)
  useEffect(() => {
    const clips = videoClipsRef.current
    const audioRefs = audioClipRefsRef.current
    if ((clips.length === 0 && audioRefs.length === 0) || durationsLoaded) return

    // Wait for preloader to have all blobs ready
    const allPaths = [...clips.map((c) => c.path), ...audioRefs.map((a) => a.path)]
    const allReady = allPaths.every((p) => preloader.getBlobUrl(p) !== null)
    if (!allReady) return

    let cancelled = false
    const loadDurations = async () => {
      const durations = new Map<string, number>()

      const videoPromises = clips.map(async (clip) => {
        try {
          const blobUrl = preloader.getBlobUrl(clip.path)
          if (!blobUrl) return

          const duration = await new Promise<number>((resolve, reject) => {
            const video = document.createElement('video')
            video.preload = 'metadata'
            video.onloadedmetadata = () => {
              resolve(video.duration)
            }
            video.onerror = () => {
              reject(new Error('Failed to load video metadata'))
            }
            // Use blob URL from preloader — no network fetch needed
            video.src = blobUrl
          })

          durations.set(clip.id, duration)
        } catch (err) {
          console.error(`Failed to load duration for ${clip.name}:`, err)
        }
      })

      const audioPromises = audioRefs.map(async (af) => {
        try {
          const blobUrl = preloader.getBlobUrl(af.path)
          if (!blobUrl) return

          const duration = await new Promise<number>((resolve, reject) => {
            const audio = new Audio()
            audio.preload = 'metadata'
            audio.onloadedmetadata = () => {
              resolve(audio.duration)
            }
            audio.onerror = () => {
              reject(new Error('Failed to load audio metadata'))
            }
            // Use blob URL from preloader — no network fetch needed
            audio.src = blobUrl
          })

          durations.set(af.id, duration)
        } catch (err) {
          console.error(`Failed to load audio duration for ${af.name}:`, err)
        }
      })

      await Promise.all([...videoPromises, ...audioPromises])

      if (!cancelled) {
        loadedDurationsRef.current = durations
        setDurationsLoaded(true)
      }
    }

    loadDurations()
    return () => {
      cancelled = true
    }
  }, [durationsLoaded, preloader])

  // Fingerprint that changes when any clip has a stale/placeholder duration
  const clipDurationFingerprint = timelineState.clips
    .map((c) => `${c.id}:${c.originalDuration}`)
    .join(',')
  // RFC-1035: audio-only timelines (zero video clips, e.g. extract/split-audio
  // restored on reopen) must still get real durations applied and totalDuration
  // corrected — so track audio identity/duration too and rerun when it changes.
  const audioClipDurationFingerprint = timelineState.audioClips
    .map((c) => `${c.id}:${c.originalDuration}:${c.sourceNodeId ?? ''}:${c.sourcePath ?? ''}`)
    .join(',')

  // Apply loaded durations to video and audio clips once both are ready
  useEffect(() => {
    if (!durationsLoaded || (clipsLength === 0 && audioClipsLength === 0)) return
    const durations = loadedDurationsRef.current
    if (durations.size === 0) return

    const currentState = timelineStateRef.current
    // RFC-1035 (RC2b): shared production helper applies real durations to video +
    // audio clips (audio routed through resolveAudioClipRealDuration; audio-only
    // timelines handled). Single source of truth shared with TimelinePreviewPlayer.
    const result = applyRealDurationsToTimeline(currentState.clips, currentState.audioClips, durations)
    if (result.changed) {
      // T279076566: applying the async duration probe is a pure metadata refresh
      // (same clips, corrected originalDuration). Use the history-PRESERVING
      // refresh so the undo stack survives the probe completing mid-session —
      // restoreState here previously wiped `past`/`future`, disabling Undo.
      const reconciled = { ...currentState, clips: currentState.magnetEnabled ? packMainTrack(result.clips) : result.clips,
        audioClips: result.audioClips, totalDuration: result.totalDuration }
      reconciled.totalDuration = computeTimelineDuration(reconciled.clips, reconciled.audioClips)
      if (inputMediaFallbackFingerprintRef.current === timelineEditablePlacementFingerprint(currentState)) {
        inputMediaFallbackFingerprintRef.current = timelineEditablePlacementFingerprint(reconciled)
      }
      timelineQaLog({ event: 'source-duration-reconciled', nodeId, magnetic: currentState.magnetEnabled,
        before: currentState.clips.map((clip) => [clip.id, clip.startOffset, clip.effectiveDuration]),
        after: reconciled.clips.map((clip) => [clip.id, clip.startOffset, clip.effectiveDuration]),
      })
      refreshTimelineState(reconciled, true)
    }
  }, [durationsLoaded, clipsLength, audioClipsLength, clipDurationFingerprint, audioClipDurationFingerprint, refreshTimelineState, nodeId])

  // The parent VideoEditorNode reads the current node/YDoc value. Avoid a
  // second self-read through useNodes(); that snapshot can lag and replay stale
  // full configs over Director-authored updates.
  const savedTimelineConfig = useMemo(
    () =>
      timelineConfig &&
      isPristineTimelineRenderConfig(timelineConfig) &&
      (initialClips.length > 0 || initialAudioClips.length > 0)
        ? undefined
        : timelineConfig,
    [initialAudioClips.length, initialClips.length, timelineConfig],
  )
  const savedTimelineConfigFingerprint = useMemo(
    () => timelineConfigFingerprint(savedTimelineConfig),
    [savedTimelineConfig],
  )
  const [lastPersistedTimelineFingerprint, setLastPersistedTimelineFingerprint] = useState(
    savedTimelineConfigFingerprint,
  )
  const lastPersistedTimelineFingerprintRef = useRef(savedTimelineConfigFingerprint)
  const [lastPersistedTimelineEditRevision, setLastPersistedTimelineEditRevision] = useState(editRevision)
  const lastPersistedTimelineEditRevisionRef = useRef(editRevision)
  const restoredSavedTimelineFingerprintRef = useRef<string | null>(
    savedTimelineConfigFingerprint || null,
  )
  const inputMediaFallbackFingerprintRef = useRef<string | null>(null)
  const inputMediaHasUnresolvedDurations = useCallback(() => {
    const loadedDurations = loadedDurationsRef.current
    return (
      videoClips.some(
        (clip) =>
          coerceTimelineDurationHint(clip.duration) === undefined &&
          coerceTimelineDurationHint(loadedDurations.get(clip.id)) === undefined,
      ) ||
      audioClipRefs.some(
        (clip) =>
          coerceTimelineDurationHint(clip.duration) === undefined &&
          coerceTimelineDurationHint(loadedDurations.get(clip.id)) === undefined,
      )
    )
  }, [videoClips, audioClipRefs])
  const provisionalFallbackConfigHeld =
    !savedTimelineConfig &&
    inputMediaFallbackFingerprintRef.current !== null &&
    inputMediaHasUnresolvedDurations()
  const shouldHoldProvisionalFallbackConfig = useCallback(
    (origin: 'update-button' | 'close' | 'export' | 'autosave' | TimelineExitFlushOrigin) => {
      if (provisionalFallbackConfigHeld) {
        timelineQaLog({
          event: 'timeline-config-provisional-fallback-held',
          origin,
          nodeId,
          reason: 'connected-media-duration-unknown',
          videoClipCount: videoClips.length,
          audioClipCount: audioClipRefs.length,
        })
      }
      return provisionalFallbackConfigHeld
    },
    [
      audioClipRefs.length,
      nodeId,
      provisionalFallbackConfigHeld,
      videoClips.length,
    ],
  )

  useEffect(() => {
    setLastPersistedTimelineFingerprint(savedTimelineConfigFingerprint)
    lastPersistedTimelineFingerprintRef.current = savedTimelineConfigFingerprint
  }, [savedTimelineConfigFingerprint])

  // Initialize clips on mount, or merge new clips from edge changes into the
  // canonical in_video_config-restored state.
  // Note: timelineState.clips.length and timelineState.audioClips.length are
  // intentionally excluded from deps. Including them causes the merge effect
  // to re-fire after every clip add/remove/split, which re-adds user-removed
  // clips because the merge logic treats missing sourceNodeIds as "new" clips
  // from edges. The ref is used inside the body for the conditional check.
  useEffect(() => {
    const currentClipsLength = timelineStateRef.current.clips.length
    // Stable signature of the current edge-connected source-id set (video +
    // audio). Only a change to THIS set is a genuine edge topology change.
    const edgeSourceKey = computeConnectedSourceKey(
      connectedVideoSourceIds,
      connectedAudioSourceIds,
    )
    const edgeTopologyChanged =
      prevEdgeSourceKeyRef.current !== null && prevEdgeSourceKeyRef.current !== edgeSourceKey
    prevEdgeSourceKeyRef.current = edgeSourceKey
    const previousConnectedVideoSourceIds = prevConnectedVideoSourceIdsRef.current
    const previousConnectedAudioSourceIds = prevConnectedAudioSourceIdsRef.current
    const resolvedVideoSourceIds = new Set(initialClips.map((clip) => clip.sourceNodeId))
    const resolvedAudioSourceIds = new Set(initialAudioClips.map((clip) => clip.sourceNodeId))
    const newlyResolvedPendingVideoSourceIds = new Set(
      [...pendingConnectedVideoSourceIdsRef.current].filter((sourceId) => resolvedVideoSourceIds.has(sourceId)),
    )
    const newlyResolvedPendingAudioSourceIds = new Set(
      [...pendingConnectedAudioSourceIdsRef.current].filter((sourceId) => resolvedAudioSourceIds.has(sourceId)),
    )
    pendingConnectedVideoSourceIdsRef.current = updatePendingConnectedSourceIds({
      previousConnectedSourceIds: previousConnectedVideoSourceIds,
      connectedSourceIds: connectedVideoSourceIdSet,
      resolvedSourceIds: resolvedVideoSourceIds,
      pendingSourceIds: pendingConnectedVideoSourceIdsRef.current,
    })
    pendingConnectedAudioSourceIdsRef.current = updatePendingConnectedSourceIds({
      previousConnectedSourceIds: previousConnectedAudioSourceIds,
      connectedSourceIds: connectedAudioSourceIdSet,
      resolvedSourceIds: resolvedAudioSourceIds,
      pendingSourceIds: pendingConnectedAudioSourceIdsRef.current,
    })
    prevConnectedVideoSourceIdsRef.current = new Set(connectedVideoSourceIdSet)
    prevConnectedAudioSourceIdsRef.current = new Set(connectedAudioSourceIdSet)

    timelineLog({
      event: 'merge-init-effect',
      branch: !hasInitializedRef.current ? 'init' : edgeTopologyChanged ? 'reconcile' : 'metadata-refresh',
      initialClipCount: initialClips.length,
      currentClipCount: currentClipsLength,
      edgeTopologyChanged,
    })

    if (!hasInitializedRef.current) {
      // One-time fresh load. Selecting this branch by `hasInitializedRef` (not by
      // `currentClipsLength === 0`) means that deleting the LAST clip and then
      // hitting a background `allNodes`/status churn is NOT mistaken for a fresh
      // load (Finding A), so a saved empty timeline never gets reseeded.
      if (savedTimelineConfig) {
        const restoredState = parseTimelineRenderConfig(savedTimelineConfig, initialClips, initialAudioClips, {
          connectedVideoSourceIds: connectedVideoSourceIdSet,
          connectedAudioSourceIds: connectedAudioSourceIdSet,
        })
        const configHash = timelineConfigTraceHash(savedTimelineConfig)
        const [configSizeBytes, configDigest] = configHash.split(':')
        const timelineItemCount = restoredState.clips.length + restoredState.audioClips.length
        emitTimelineSourceEvent({
          event: 'timeline-modal-loaded-in-video-config',
          source: 'in_video_config',
          sourceField: 'in_video_config',
          consumer: 'modal',
          trigger: 'modal-initial-load',
          configFound: true,
          configHash,
          configSizeBytes: Number(configSizeBytes),
          configDigest,
          configStatus: timelineItemCount === 0 ? 'found_empty' : 'found_with_items',
          loadResult: timelineItemCount === 0 ? 'found-empty-timeline' : 'applied-items',
          nodeId,
          connectedVideoCount: initialClips.length,
          connectedAudioCount: initialAudioClips.length,
          savedClipCount: savedTimelineConfig.timeline?.clips?.length ?? 0,
          savedAudioClipCount: savedTimelineConfig.timeline?.audio_tracks?.length ?? 0,
          restoredClipCount: restoredState.clips.length,
          restoredAudioClipCount: restoredState.audioClips.length,
          savedConfigClipCount: savedTimelineConfig.timeline?.clips?.length ?? 0,
          savedConfigAudioClipCount: savedTimelineConfig.timeline?.audio_tracks?.length ?? 0,
          restoredUiClipCount: restoredState.clips.length,
          restoredUiAudioClipCount: restoredState.audioClips.length,
          uiClipCount: restoredState.clips.length,
          uiAudioClipCount: restoredState.audioClips.length,
          clipCount: restoredState.clips.length,
          audioClipCount: restoredState.audioClips.length,
          trackCount: restoredState.videoTrackCount + restoredState.audioTrackCount,
          timelineItemCount,
          duration_ms: Math.round(restoredState.totalDuration * 1000),
        })
        restoreState(restoredState)
        const restoredFingerprint = timelineConfigFingerprint(buildTimelineRenderConfig(restoredState))
        setLastPersistedTimelineFingerprint(restoredFingerprint)
        lastPersistedTimelineFingerprintRef.current = restoredFingerprint
        restoredSavedTimelineFingerprintRef.current = savedTimelineConfigFingerprint
        inputMediaFallbackFingerprintRef.current = null
      } else {
        const totalDuration = computeTimelineDuration(initialClips, initialAudioClips)
        const fallbackState = {
          clips: initialClips,
          audioClips: initialAudioClips,
          playheadPosition: 0,
          zoomLevel: 50,
          isPlaying: false,
          selectedClipIds: new Set<string>(),
          totalDuration,
          videoTrackCount: 1,
          audioTrackCount: 1,
          copiedClip: null,
          copiedClipType: null,
          mutedVideoTracks: new Set<number>(),
          mutedAudioTracks: new Set<number>(),
          magnetEnabled: true,
        }
        restoreState(fallbackState)
        inputMediaFallbackFingerprintRef.current = timelineEditablePlacementFingerprint(fallbackState)
        restoredSavedTimelineFingerprintRef.current = null
        emitTimelineSourceEvent({
          event: 'timeline-modal-built-from-input-media',
          source: 'connected-input-media',
          consumer: 'modal',
          trigger: 'modal-initial-load',
          configFound: false,
          nodeId,
          connectedVideoCount: initialClips.length,
          connectedAudioCount: initialAudioClips.length,
          clipCount: initialClips.length,
          audioClipCount: initialAudioClips.length,
          timelineItemCount: initialClips.length + initialAudioClips.length,
          duration_ms: Math.round(totalDuration * 1000),
        })
      }
      hasInitializedRef.current = true
      setTimelineLoaded(true)
    } else if (
      savedTimelineConfig &&
      savedTimelineConfigFingerprint &&
      restoredSavedTimelineFingerprintRef.current !== savedTimelineConfigFingerprint &&
      inputMediaFallbackFingerprintRef.current !== null
    ) {
      const currentState = timelineStateRef.current
      const currentFallbackFingerprint = timelineEditablePlacementFingerprint(currentState)
      if (currentFallbackFingerprint === inputMediaFallbackFingerprintRef.current) {
        const restoredState = parseTimelineRenderConfig(savedTimelineConfig, initialClips, initialAudioClips, {
          connectedVideoSourceIds: connectedVideoSourceIdSet,
          connectedAudioSourceIds: connectedAudioSourceIdSet,
        })
        const configHash = timelineConfigTraceHash(savedTimelineConfig)
        const [configSizeBytes, configDigest] = configHash.split(':')
        const timelineItemCount = restoredState.clips.length + restoredState.audioClips.length
        emitTimelineSourceEvent({
          event: 'timeline-modal-loaded-in-video-config',
          source: 'in_video_config',
          sourceField: 'in_video_config',
          consumer: 'modal',
          trigger: 'modal-late-config-load',
          configFound: true,
          configHash,
          configSizeBytes: Number(configSizeBytes),
          configDigest,
          configStatus: timelineItemCount === 0 ? 'found_empty' : 'found_with_items',
          loadResult: timelineItemCount === 0 ? 'found-empty-timeline' : 'applied-items',
          nodeId,
          connectedVideoCount: initialClips.length,
          connectedAudioCount: initialAudioClips.length,
          savedClipCount: savedTimelineConfig.timeline?.clips?.length ?? 0,
          savedAudioClipCount: savedTimelineConfig.timeline?.audio_tracks?.length ?? 0,
          restoredClipCount: restoredState.clips.length,
          restoredAudioClipCount: restoredState.audioClips.length,
          savedConfigClipCount: savedTimelineConfig.timeline?.clips?.length ?? 0,
          savedConfigAudioClipCount: savedTimelineConfig.timeline?.audio_tracks?.length ?? 0,
          restoredUiClipCount: restoredState.clips.length,
          restoredUiAudioClipCount: restoredState.audioClips.length,
          uiClipCount: restoredState.clips.length,
          uiAudioClipCount: restoredState.audioClips.length,
          clipCount: restoredState.clips.length,
          audioClipCount: restoredState.audioClips.length,
          trackCount: restoredState.videoTrackCount + restoredState.audioTrackCount,
          timelineItemCount,
          duration_ms: Math.round(restoredState.totalDuration * 1000),
        })
        restoreState({ ...restoredState, playheadPosition: Math.min(currentState.playheadPosition, restoredState.totalDuration),
          zoomLevel: currentState.zoomLevel, isPlaying: currentState.isPlaying, selectedClipIds: currentState.selectedClipIds })
        const restoredFingerprint = timelineConfigFingerprint(buildTimelineRenderConfig(restoredState))
        setLastPersistedTimelineFingerprint(restoredFingerprint)
        lastPersistedTimelineFingerprintRef.current = restoredFingerprint
        restoredSavedTimelineFingerprintRef.current = savedTimelineConfigFingerprint
        inputMediaFallbackFingerprintRef.current = null
      } else {
        emitTimelineSourceEvent({
          event: 'timeline-modal-late-config-skipped',
          source: 'in_video_config',
          sourceField: 'in_video_config',
          consumer: 'modal',
          trigger: 'modal-late-config-load',
          configFound: true,
          loadResult: 'skipped-local-state-changed',
          nodeId,
          connectedVideoCount: initialClips.length,
          connectedAudioCount: initialAudioClips.length,
          savedClipCount: savedTimelineConfig.timeline?.clips?.length ?? 0,
          savedAudioClipCount: savedTimelineConfig.timeline?.audio_tracks?.length ?? 0,
          clipCount: currentState.clips.length,
          audioClipCount: currentState.audioClips.length,
          timelineItemCount: currentState.clips.length + currentState.audioClips.length,
          duration_ms: Math.round(currentState.totalDuration * 1000),
        })
        inputMediaFallbackFingerprintRef.current = null
      }
    } else if (edgeTopologyChanged) {
      // Real edge source-set change → FULL reconcile (add new edge clips + prune
      // disconnected + refresh refs) and RESET undo history (undo must not
      // resurrect a source that is no longer edge-connected).
      const currentState = timelineStateRef.current
      const initialClipsBySourceId = new Map(initialClips.map(c => [c.sourceNodeId, c]))
      const existingSourceIds = new Set(currentState.clips.map(c => c.sourceNodeId))
      const newlyConnectedVideoSourceIds = new Set(
        [...connectedVideoSourceIdSet].filter((sourceId) => !previousConnectedVideoSourceIds.has(sourceId)),
      )
      const materializableVideoSourceIds = computeMaterializableSourceIds({
        existingSourceIds,
        newlyConnectedSourceIds: newlyConnectedVideoSourceIds,
        newlyResolvedPendingSourceIds: newlyResolvedPendingVideoSourceIds,
      })
      const newClips = initialClips.filter(
        (clip) => materializableVideoSourceIds.has(clip.sourceNodeId),
      )

      // Prune disconnected clips and refresh canonical asset refs from edge-resolved inputs.
      // RFC-1035 (Fix 3): KEEP timeline-owned literal clips (extract/split-audio
      // stems, uploads) across an unrelated edge-topology change. Only graph-backed
      // clips (symbolic `<id>.out_*` ref) whose source is no longer edge-connected
      // are pruned. A literal clip has no live input edge, so `initialSourceIds`
      // never contains it — the old membership-only filter dropped it here even
      // though it is a durable timeline artifact.
      const prunedClips = currentState.clips
        .filter(c => connectedVideoSourceIdSet.has(c.sourceNodeId) || !isSymbolicTimelineSourceRef(c.sourceRef ?? c.sourcePath))
        .map(c => {
          const currentRef = initialClipsBySourceId.get(c.sourceNodeId)
          return currentRef
            ? { ...c, sourcePath: currentRef.sourcePath, sourceRef: currentRef.sourceRef, label: currentRef.label }
            : c
        })

      const mergedClips = appendMainTrackReferences(prunedClips, newClips, currentState.magnetEnabled)
      const videoRefsChanged = prunedClips.some((clip) => {
        const previous = currentState.clips.find(c => c.sourceNodeId === clip.sourceNodeId)
        return previous?.sourcePath !== clip.sourcePath || previous?.label !== clip.label
      })

      // Also merge + prune audio clips
      const initialAudioClipsBySourceId = new Map(initialAudioClips.map(c => [c.sourceNodeId, c]))
      const existingAudioSourceIds = new Set(currentState.audioClips.map(c => c.sourceNodeId))
      const newlyConnectedAudioSourceIds = new Set(
        [...connectedAudioSourceIdSet].filter((sourceId) => !previousConnectedAudioSourceIds.has(sourceId)),
      )
      const materializableAudioSourceIds = computeMaterializableSourceIds({
        existingSourceIds: existingAudioSourceIds,
        newlyConnectedSourceIds: newlyConnectedAudioSourceIds,
        newlyResolvedPendingSourceIds: newlyResolvedPendingAudioSourceIds,
      })
      const newAudioClipRefs = initialAudioClips.filter(
        (clip) => materializableAudioSourceIds.has(clip.sourceNodeId),
      )
      // RFC-1035 (Fix 3): mirror the video-clip rule for audio — KEEP
      // timeline-owned literal audio clips (extract/split-audio WAVs) across
      // an unrelated edge change; only prune graph-backed symbolic-ref clips
      // whose input disconnected.
      const prunedAudioClips = currentState.audioClips
        .filter(c => connectedAudioSourceIdSet.has(c.sourceNodeId) || !isSymbolicTimelineSourceRef(c.sourceRef ?? c.sourcePath))
        .map(c => {
          const currentRef = initialAudioClipsBySourceId.get(c.sourceNodeId)
          return currentRef
            ? {
                ...c,
                sourcePath: currentRef.sourcePath,
                sourceRef: currentRef.sourceRef,
                label: c.label?.trim() ? c.label : currentRef.label,
                audioKind: c.audioKind ?? currentRef.audioKind,
              }
            : c
        })

      const videoTimelineEndS = computeTimelineDuration(mergedClips, [])
      let accumulatedAudioClips = [...prunedAudioClips]
      const newAudioClips = newAudioClipRefs.flatMap((clip) => {
        const duration = loadedDurationsRef.current.get(clip.sourceNodeId) ?? clip.originalDuration
        const timelineEndS = videoTimelineEndS > 0
          ? videoTimelineEndS
          : Math.max(currentState.totalDuration, clip.startOffset + duration)
        const fillClips = buildSelectiveAudioFillClips({
          sourceNodeId: clip.sourceNodeId,
          sourcePath: clip.sourcePath,
          sourceRef: clip.sourceRef,
          originalDuration: duration,
          label: clip.label,
          audioKind: clip.audioKind,
          role: clip.role,
          volume: clip.volume,
          muted: clip.muted,
          startOffset: clip.startOffset,
          trackIndex: clip.trackIndex,
          timelineEndS,
          existingAudioClips: accumulatedAudioClips,
        })
        const logPayload = {
          event: fillClips.length > 0 ? 'selective-audio-fill-created' : 'selective-audio-fill-empty',
          trace_id: `selective-master-track-audio:${nodeId}:${clip.sourceNodeId}`,
          source: 'edge-merge',
          nodeId,
          sourceNodeId: clip.sourceNodeId,
          sourcePath: clip.sourcePath,
          sourceDurationS: Number(duration.toFixed(3)),
          startOffsetS: Number(clip.startOffset.toFixed(3)),
          timelineEndS: Number(timelineEndS.toFixed(3)),
          existingAudioClipCount: accumulatedAudioClips.length,
          existingUnmutedAudioClipCount: accumulatedAudioClips.filter(
            (existingClip) => !existingClip.muted,
          ).length,
          createdClipCount: fillClips.length,
          createdRanges: fillClips.map((createdClip) => ({
            startOffsetS: Number(createdClip.startOffset.toFixed(3)),
            durationS: Number(createdClip.effectiveDuration.toFixed(3)),
            sourceInS: Number(createdClip.trimStart.toFixed(3)),
          })),
        }
        selectiveAudioLog(logPayload)
        emitTimelineNadEvent('dw:selective-master-track-audio:gap-fill', logPayload)
        accumulatedAudioClips = [...accumulatedAudioClips, ...fillClips]
        return fillClips
      })
      const audioRefsChanged = prunedAudioClips.some((clip) => {
        const previous = currentState.audioClips.find(c => c.sourceNodeId === clip.sourceNodeId)
        return previous?.sourcePath !== clip.sourcePath ||
          previous?.label !== clip.label ||
          previous?.audioKind !== clip.audioKind
      })

      // Edge topology genuinely changed → apply the fully reconciled state and
      // RESET undo history.
      const shouldApply =
        newClips.length > 0 ||
        prunedClips.length !== currentState.clips.length ||
        newAudioClips.length > 0 ||
        prunedAudioClips.length !== currentState.audioClips.length ||
        videoRefsChanged ||
        audioRefsChanged

      if (shouldApply) {
        const nextAudioClips = [...prunedAudioClips, ...newAudioClips]
        restoreState({
          ...currentState,
          clips: mergedClips,
          audioClips: nextAudioClips,
          totalDuration: computeTimelineDuration(mergedClips, nextAudioClips),
          videoTrackCount: Math.max(currentState.videoTrackCount, ...mergedClips.map((clip) => clip.trackIndex + 1), 1),
          audioTrackCount: Math.max(currentState.audioTrackCount, ...nextAudioClips.map((clip) => clip.trackIndex + 1), 1),
        }, { preserveEditRevision: true })
      }
    } else {
      // Edges UNCHANGED → apply METADATA-ONLY ref refreshes (sourcePath/label) to
      // still-present clips, PRESERVING undo history. Do NOT add newClips or prune
      // against the current timeline — a user-owned deletion must survive a
      // background `allNodes`/status churn (Finding B). Source-level duration
      // corrections flow through the separate duration-apply effect.
      const currentState = timelineStateRef.current
      const initialClipsBySourceId = new Map(initialClips.map((c) => [c.sourceNodeId, c]))
      const initialAudioClipsBySourceId = new Map(initialAudioClips.map((c) => [c.sourceNodeId, c]))
      const refreshedClips = currentState.clips.map((c) => {
        const ref = initialClipsBySourceId.get(c.sourceNodeId)
        return ref ? { ...c, sourcePath: ref.sourcePath, sourceRef: ref.sourceRef, label: ref.label } : c
      })
      const refreshedAudioClips = currentState.audioClips.map((c) => {
        const ref = initialAudioClipsBySourceId.get(c.sourceNodeId)
        return ref
          ? {
              ...c,
              sourcePath: ref.sourcePath,
              sourceRef: ref.sourceRef,
              label: c.label?.trim() ? c.label : ref.label,
              audioKind: c.audioKind ?? ref.audioKind,
            }
          : c
      })
      const videoRefsChanged = refreshedClips.some(
        (clip, i) =>
          clip.sourcePath !== currentState.clips[i].sourcePath ||
          clip.label !== currentState.clips[i].label,
      )
      const audioRefsChanged = refreshedAudioClips.some(
        (clip, i) =>
          clip.sourcePath !== currentState.audioClips[i].sourcePath ||
          clip.label !== currentState.audioClips[i].label ||
          clip.audioKind !== currentState.audioClips[i].audioKind,
      )
      const materializedVideoClips = initialClips.filter(
        (clip) =>
          newlyResolvedPendingVideoSourceIds.has(clip.sourceNodeId) &&
          !currentState.clips.some((current) => current.sourceNodeId === clip.sourceNodeId),
      )
      const nextVideoClips = appendMainTrackReferences(refreshedClips, materializedVideoClips, currentState.magnetEnabled && materializedVideoClips.length > 0)
      const materializedAudioClipRefs = initialAudioClips.filter(
        (clip) =>
          newlyResolvedPendingAudioSourceIds.has(clip.sourceNodeId) &&
          !currentState.audioClips.some((current) => current.sourceNodeId === clip.sourceNodeId),
      )
      let nextAudioClips = [...refreshedAudioClips]
      const videoTimelineEndS = computeTimelineDuration(nextVideoClips, [])
      for (const clip of materializedAudioClipRefs) {
        const duration = loadedDurationsRef.current.get(clip.sourceNodeId) ?? clip.originalDuration
        const timelineEndS = videoTimelineEndS > 0
          ? videoTimelineEndS
          : Math.max(currentState.totalDuration, clip.startOffset + duration)
        nextAudioClips = [
          ...nextAudioClips,
          ...buildSelectiveAudioFillClips({
            sourceNodeId: clip.sourceNodeId,
            sourcePath: clip.sourcePath,
            sourceRef: clip.sourceRef,
            originalDuration: duration,
            label: clip.label,
            audioKind: clip.audioKind,
            role: clip.role,
            volume: clip.volume,
            muted: clip.muted,
            startOffset: clip.startOffset,
            trackIndex: clip.trackIndex,
            timelineEndS,
            existingAudioClips: nextAudioClips,
          }),
        ]
      }
      if (
        videoRefsChanged ||
        audioRefsChanged ||
        materializedVideoClips.length > 0 ||
        materializedAudioClipRefs.length > 0
      ) {
        refreshTimelineState({
          ...currentState,
          clips: nextVideoClips,
          audioClips: nextAudioClips,
          totalDuration: computeTimelineDuration(nextVideoClips, nextAudioClips),
        })
      }
    }
  }, [
    initialClips,
    initialAudioClips,
    connectedVideoSourceIds,
    connectedAudioSourceIds,
    connectedVideoSourceIdSet,
    connectedAudioSourceIdSet,
    nodeId,
    savedTimelineConfig,
    savedTimelineConfigFingerprint,
    restoreState,
    refreshTimelineState,
  ])

  // RFC-0633 round-4 (Issue 5): each `TimelineClip` owns its own per-source
  // thumbnail cache via `useClipThumbnails`. The hook resolves asset URLs
  // internally via `useProjectId` + `fetchMediaBlob`, so there is no
  // pre-resolved URL projection or batch lift here any more.

  // Playback hook
  const {
    togglePlayPause: handleTogglePlayPause,
    jumpToStart,
    jumpToEnd,
    playheadPositionRef,
  } = useTimelinePlayback({
    timelineState: playbackTimelineState,
    setPlayhead,
    togglePlayPause,
  })
  const handleUserTogglePlayPause = useCallback(() => {
    if (dreamVibePreviewUnavailable) return
    if (!timelineState.isPlaying) {
      resumeAudioContexts()
    }
    handleTogglePlayPause()
  }, [dreamVibePreviewUnavailable, handleTogglePlayPause, resumeAudioContexts, timelineState.isPlaying])
  const handleTimeEdit = useCallback((clip: TimelineClip, mode: 'reverse' | 'freeze' | 'speed_curve', points?: SpeedPoint[]) => {
    if (timelineLocked || isEditSpecPrimary) return
    if (timelineStateRef.current.isPlaying) {
      setPlayhead(playheadPositionRef.current)
      togglePlayPause()
    }
    trackTimelineEdit(projectId, nodeId, mode, 'video')
    void runTimeEdit(clip, mode, playheadPositionRef.current, points)
  }, [timelineLocked, isEditSpecPrimary, playheadPositionRef, setPlayhead, togglePlayPause, runTimeEdit, projectId, nodeId])
  const handleSpeedCurve = useCallback((clip: TimelineClip, points: SpeedPoint[]) => handleTimeEdit(clip, 'speed_curve', points), [handleTimeEdit])

  useEffect(() => {
    if (dreamVibePreviewUnavailable && timelineState.isPlaying) {
      togglePlayPause()
    }
  }, [dreamVibePreviewUnavailable, timelineState.isPlaying, togglePlayPause])

  // Selection summary — derived projection of the multi-selection set used by
  // the legacy single-selection consumers (AdjustPanel, ClipPanel highlight).
  // For multi-selection these consumers gracefully fall back to a "select one
  // clip to adjust" affordance.
  const importedTimeline = useMemo(() => isEditSpecPrimary && activeEditSpec
    ? projectEditSpecTimeline(activeEditSpec, dreamVibeSourceRevisions, timelineState, editSpecSourceMetadata.durations)
    : timelineState, [isEditSpecPrimary, activeEditSpec, dreamVibeSourceRevisions, timelineState, editSpecSourceMetadata.durations])
  const selectionSummary = useMemo(() => summarizeSelection(timelineState), [timelineState])
  const isMultiSelected = timelineState.selectedClipIds.size > 1

  // Selected clip objects for AdjustPanel — only meaningful when exactly one
  // clip is selected.
  const selectedVideoClip = useMemo(
    () =>
      !isMultiSelected && selectionSummary.type === 'video'
        ? timelineState.clips.find((c) => c.id === selectionSummary.primaryId) ?? null
        : null,
    [timelineState.clips, selectionSummary, isMultiSelected]
  )

  const selectedAudioClip = useMemo(
    () =>
      selectionSummary.type === 'audio' && !isMultiSelected
        ? timelineState.audioClips.find((c) => c.id === selectionSummary.primaryId) ?? null
        : null,
    [timelineState.audioClips, selectionSummary, isMultiSelected]
  )
  const selectedClipTypeForControls = isMultiSelected
    ? 'mixed'
    : (selectionSummary.type === 'video' || selectionSummary.type === 'audio'
        ? selectionSummary.type
        : null)
  const selectedVideoHasExtractedAudio = useMemo(
    () => selectedVideoClip
      ? hasExtractedNativeAudioForVideo(timelineState.audioClips, selectedVideoClip.id)
      : false,
    [selectedVideoClip, timelineState.audioClips],
  )

  // Route speed changes to correct handler based on clip type
  const handleSpeedChange = useCallback(
    (clipId: string, speed: number) => {
      const isAudio = timelineState.audioClips.some((c) => c.id === clipId)
      if (isAudio) {
        setAudioSpeed(clipId, speed)
      } else {
        setClipSpeed(clipId, speed)
      }
    },
    [timelineState.audioClips, setAudioSpeed, setClipSpeed]
  )

  // ScrollToClip handler — selects the clip (which highlights it in the timeline)
  const handleScrollToClip = useCallback(
    (clipId: string, clipType: 'video' | 'audio') => {
      selectClip(clipId, clipType)
    },
    [selectClip]
  )

  // Add a reference clip instance to the timeline.
  const addReferenceClipInstance = useCallback(
    (clipId: string, clipType: 'video' | 'audio', startOffset: number, trackIndex: number): boolean => {
      timelineLog({
        event: 'reference-instance-add',
        nodeId,
        clipId,
        clipType,
        startOffset,
        trackIndex,
      })
      if (clipType === 'video') {
        const ref = videoClips.find(c => c.id === clipId)
        if (!ref) return false
        const duration = loadedDurationsRef.current.get(ref.id) ?? 5
        addClip({
          id: crypto.randomUUID(),
          sourceNodeId: ref.id,
          sourcePath: ref.path,
          sourceRef: `${ref.id}.out_video`,
          originalDuration: duration,
          trimStart: 0,
          trimEnd: 0,
          effectiveDuration: duration,
          order: timelineStateRef.current.clips.length,
          startOffset: Math.max(0, startOffset),
          trackIndex: Math.max(0, Math.min(timelineStateRef.current.videoTrackCount - 1, trackIndex)),
          label: ref.name,
          speed: 1.0,
          volume: 1.0,
          muted: false,
        })
        return true
      } else {
        const ref = audioClipRefs.find(c => c.id === clipId)
        if (!ref) return false
        const duration = loadedDurationsRef.current.get(ref.id) ?? 15
        const videoTimelineEndS = computeTimelineDuration(timelineStateRef.current.clips, [])
        const timelineEndS = videoTimelineEndS > 0
          ? videoTimelineEndS
          : Math.max(timelineStateRef.current.totalDuration, startOffset + duration)
        const fillClips = buildSelectiveAudioFillClips({
          sourceNodeId: ref.id,
          sourcePath: ref.path,
          sourceRef: `${ref.id}.out_audio`,
          originalDuration: duration,
          label: ref.name,
          audioKind: ref.audioKind ?? 'audio',
          volume: 0.8,
          startOffset: Math.max(0, startOffset),
          trackIndex: Math.max(0, Math.min(timelineStateRef.current.audioTrackCount - 1, trackIndex)),
          timelineEndS,
          existingAudioClips: timelineStateRef.current.audioClips,
        })
        const logPayload = {
          event: fillClips.length > 0 ? 'selective-audio-fill-created' : 'selective-audio-fill-empty',
          trace_id: `selective-master-track-audio:${nodeId}:${ref.id}`,
          source: 'reference-add',
          nodeId,
          sourceNodeId: ref.id,
          sourcePath: ref.path,
          sourceDurationS: Number(duration.toFixed(3)),
          startOffsetS: Number(Math.max(0, startOffset).toFixed(3)),
          timelineEndS: Number(timelineEndS.toFixed(3)),
          existingAudioClipCount: timelineStateRef.current.audioClips.length,
          existingUnmutedAudioClipCount: timelineStateRef.current.audioClips.filter(
            (clip) => !clip.muted,
          ).length,
          createdClipCount: fillClips.length,
          createdRanges: fillClips.map((clip) => ({
            startOffsetS: Number(clip.startOffset.toFixed(3)),
            durationS: Number(clip.effectiveDuration.toFixed(3)),
            sourceInS: Number(clip.trimStart.toFixed(3)),
          })),
        }
        selectiveAudioLog(logPayload)
        emitTimelineNadEvent('dw:selective-master-track-audio:gap-fill', logPayload)
        if (fillClips.length > 0) {
          addAudioClips(fillClips)
          return true
        }
        return false
      }
    },
    [nodeId, videoClips, audioClipRefs, addClip, addAudioClips]
  )

  const handleAddClipToTimeline = useCallback(
    (clipId: string, clipType: 'video' | 'audio') => {
      const added = addReferenceClipInstance(
        clipId,
        clipType,
        timelineStateRef.current.playheadPosition,
        0
      )
      // RFC-0974: emit only when a clip was actually added (missing ref / empty
      // audio-fill no-ops must not record a spurious event). Best-effort, consent-gated.
      if (added && projectId) trackClipAddedToTimeline({ project_id: projectId, clip_id: clipId })
    },
    [addReferenceClipInstance, projectId]
  )

  const handleAddReferenceAt = useCallback(
    (clipId: string, clipType: 'video' | 'audio', startOffset: number, trackIndex: number) => {
      const added = addReferenceClipInstance(clipId, clipType, startOffset, trackIndex)
      // RFC-0974: same event via the drag-drop add path (only on a real add).
      if (added && projectId) trackClipAddedToTimeline({ project_id: projectId, clip_id: clipId })
    },
    [addReferenceClipInstance, projectId]
  )

  const currentTimelineConfig = useMemo(
    () => buildTimelineRenderConfig(timelineState),
    [timelineState],
  )
  const currentTimelineConfigFingerprint = useMemo(
    () => timelineConfigFingerprint(currentTimelineConfig),
    [currentTimelineConfig],
  )
  const timelineEditRevisionDirty = editRevision !== lastPersistedTimelineEditRevision
  const hasUnsavedTimelineConfig =
    !isEditSpecPrimary && timelineEditRevisionDirty && currentTimelineConfigFingerprint !== lastPersistedTimelineFingerprint
  const hasUnsavedTimelineConfigRef = useRef(hasUnsavedTimelineConfig)
  hasUnsavedTimelineConfigRef.current = hasUnsavedTimelineConfig
  const currentTimelineConfigRef = useRef(currentTimelineConfig)
  currentTimelineConfigRef.current = currentTimelineConfig
  const [audioActionBusy, setAudioActionBusy] = useState(false)
  const [runningAudioAction, setRunningAudioAction] = useState<RunningAudioAction | null>(null)
  const [audioActionError, setAudioActionError] = useState<AudioActionErrorState | null>(null)
  const [pendingAudioAction, setPendingAudioAction] = useState<PendingAudioAction | null>(null)
  const [videoEditorAudioActions, setVideoEditorAudioActions] = useState<VideoEditorAudioActionsResponse | null>(null)

  const selectedAudioActionTarget = useMemo<SelectedAudioActionTarget | null>(() => {
    if (isMultiSelected) return null
    if (selectionSummary.type === 'video' && selectedVideoClip) {
      return {
        targetId: `${selectedVideoClip.id}:original-audio`,
        sourceClipId: selectedVideoClip.id,
        sourceClipType: 'video',
      }
    }
    if (selectionSummary.type === 'audio' && selectedAudioClip) {
      return {
        targetId: selectedAudioClip.id,
        sourceClipId: selectedAudioClip.id,
        sourceClipType: 'audio',
      }
    }
    return null
  }, [
    isMultiSelected,
    selectedAudioClip,
    selectedVideoClip,
    selectionSummary.type,
  ])

  useEffect(() => {
    if (!projectId || isEditSpecPrimary) {
      setVideoEditorAudioActions(null)
      return
    }
    let cancelled = false
    void getVideoEditorAudioActions(projectId, nodeId, canvasId).then(
      (response) => {
        if (!cancelled) setVideoEditorAudioActions(response)
      },
      (error) => {
        if (cancelled) return
        setVideoEditorAudioActions(null)
        audioEditorActionLog({
          event: 'timeline-audio-actions-read-failed',
          nodeId,
          error: error instanceof Error ? error.message : String(error),
        })
      },
    )
    return () => {
      cancelled = true
    }
  }, [canvasId, currentTimelineConfigFingerprint, isEditSpecPrimary, nodeId, projectId])

  const selectedSplitDialogueAvailable = useMemo(() => {
    if (!selectedAudioActionTarget) return false
    const target = videoEditorAudioActions?.targets.find(
      (candidate) => candidate.id === selectedAudioActionTarget.targetId,
    )
    return Boolean(
      target?.actions.some((action) => action.id === 'split_dialogue' && action.state === 'available'),
    )
  }, [selectedAudioActionTarget, videoEditorAudioActions])

  const audioActionOperationStates = useMemo<TimelineClipOperationStateMap>(() => {
    const states: TimelineClipOperationStateMap = {}
    if (pendingAudioAction) {
      states[pendingAudioAction.sourceClipId] = {
        status: 'running',
        actionId: pendingAudioAction.actionId,
      }
      return states
    }
    if (runningAudioAction) {
      states[runningAudioAction.sourceClipId] = {
        status: 'running',
        actionId: runningAudioAction.actionId,
      }
      return states
    }
    if (audioActionError) {
      states[audioActionError.sourceClipId] = {
        status: 'error',
        message: audioActionError.message,
      }
    }
    return states
  }, [audioActionError, pendingAudioAction, runningAudioAction])
  const selectedClipAudioOperationRunning = Boolean(
    selectedAudioActionTarget &&
      audioActionOperationStates[selectedAudioActionTarget.sourceClipId]?.status === 'running',
  )
  const selectedTimelineEditLocked = timelineLocked || selectedClipAudioOperationRunning
  const selectedClipAudioActionError =
    audioActionError && audioActionError.sourceClipId === selectedAudioActionTarget?.sourceClipId
      ? audioActionError.message
      : null

  const handleExtractNativeAudio = useCallback(() => {
    if (!selectedVideoClip || selectedVideoHasExtractedAudio) return
    trackTimelineEdit(projectId, nodeId, 'extract_native_audio', 'video')
    extractNativeVideoAudio(selectedVideoClip.id)
  }, [extractNativeVideoAudio, selectedVideoClip, selectedVideoHasExtractedAudio, projectId, nodeId])

  const applyAudioActionResponse = useCallback(
    (
      response: VideoEditorAudioActionRunResponse,
      target: NonNullable<typeof selectedAudioActionTarget>,
    ) => {
      if (response.status === 'completed') {
        if (response.action_id === 'split_dialogue') {
          if (response.speech_segments.length > 0) {
            const segments = response.speech_segments.map((segment) => ({
              start_s: segment.start_s,
              end_s: segment.end_s,
            }))
            const splitResult = target.sourceClipType === 'video'
              ? splitClipByDialogue(target.sourceClipId, segments)
              : splitAudioClipByDialogue(target.sourceClipId, segments)
            if (splitResult.changed) {
              toast.success(t('adjustPanel.splitDialogueComplete', { count: splitResult.clipCount }))
            } else {
              toast.info(t('adjustPanel.splitDialogueNoSplit', 'No dialogue split was needed.'))
            }
          } else {
            toast.info(t('adjustPanel.splitDialogueNoSpeech', 'No spoken dialogue was detected.'))
          }
          setPendingAudioAction(null)
          return
        }
        const clips = response.derived_audio_clips.map(timelineAudioClipFromDerived)
        if (clips.length > 0) {
          applyDerivedAudioClips(
            target.sourceClipId,
            target.sourceClipType,
            response.action_id === 'change_voice' ? 'change_voice' : 'isolate_audio',
            clips,
            response.action_id === 'change_voice'
              ? 'Change voice'
              : 'Isolate vocals and background',
          )
          toast.success(response.message ?? t('adjustPanel.audioActionComplete', 'Audio action complete'))
        }
        setPendingAudioAction(null)
        return
      }
      if (response.action_id === 'isolate_audio' && response.job_id) {
        setPendingAudioAction({
          actionId: 'isolate_audio',
          targetId: response.target_id,
          jobId: response.job_id,
          sourceClipId: target.sourceClipId,
          sourceClipType: target.sourceClipType,
        })
        toast.info(response.message ?? t('adjustPanel.audioActionProcessing', 'Audio action is processing'))
      }
    },
    [applyDerivedAudioClips, splitAudioClipByDialogue, splitClipByDialogue, t],
  )

  const runSelectedAudioAction = useCallback(
    async (actionId: AudioActionId, voice?: CharacterVoiceCatalogueEntry) => {
      if (!projectId || !selectedAudioActionTarget) return
      const actionTarget = selectedAudioActionTarget
      setAudioActionBusy(true)
      setRunningAudioAction({ ...actionTarget, actionId })
      setAudioActionError(null)
      audioEditorActionLog({
        event: 'timeline-audio-action-start',
        nodeId,
        actionId,
        targetId: actionTarget.targetId,
      })
      try {
        const response = await runVideoEditorAudioAction(
          projectId,
          nodeId,
          canvasId,
          actionId,
          actionTarget.targetId,
          {
            timelineConfig: currentTimelineConfigRef.current,
            voiceRef: voice?.voice_ref,
            voiceId: voice?.voice_id,
          },
        )
        applyAudioActionResponse(response, actionTarget)
        audioEditorActionLog({
          event: 'timeline-audio-action-response',
          nodeId,
          actionId,
          targetId: actionTarget.targetId,
          status: response.status,
          jobId: response.job_id,
          derivedClipCount: response.derived_audio_clips.length,
          speechSegmentCount: response.speech_segments.length,
        })
      } catch (error) {
        const message = error instanceof Error ? error.message : t('nodeActions.unknownError')
        setAudioActionError({ sourceClipId: actionTarget.sourceClipId, message })
        toast.error(message)
        audioEditorActionLog({
          event: 'timeline-audio-action-error',
          nodeId,
          actionId,
          targetId: actionTarget.targetId,
          error: message,
        })
        if (actionId === 'change_voice') {
          throw error instanceof Error ? error : new Error(message)
        }
      } finally {
        setAudioActionBusy(false)
        setRunningAudioAction(null)
      }
    },
    [applyAudioActionResponse, canvasId, nodeId, projectId, selectedAudioActionTarget, t],
  )

  useEffect(() => {
    if (!projectId || !pendingAudioAction) return
    let cancelled = false
    let inFlight = false
    const poll = window.setInterval(() => {
      if (inFlight) return
      inFlight = true
      void runVideoEditorAudioAction(
        projectId,
        nodeId,
        canvasId,
        pendingAudioAction.actionId,
        pendingAudioAction.targetId,
        {
          splitJobId: pendingAudioAction.jobId,
          timelineConfig: currentTimelineConfigRef.current,
        },
      ).then(
        (response) => {
          if (cancelled) return
          applyAudioActionResponse(response, pendingAudioAction)
        },
        (error) => {
          if (cancelled) return
          const message = error instanceof Error ? error.message : t('nodeActions.unknownError')
          setAudioActionError({ sourceClipId: pendingAudioAction.sourceClipId, message })
          setPendingAudioAction(null)
          toast.error(message)
          audioEditorActionLog({
            event: 'timeline-audio-action-poll-error',
            nodeId,
            actionId: pendingAudioAction.actionId,
            targetId: pendingAudioAction.targetId,
            jobId: pendingAudioAction.jobId,
            error: message,
          })
        },
      ).finally(() => {
        inFlight = false
      })
    }, 2500)
    return () => {
      cancelled = true
      window.clearInterval(poll)
    }
  }, [applyAudioActionResponse, canvasId, nodeId, pendingAudioAction, projectId, t])

  const persistTimelineConfig = useCallback(
    (origin: 'update-button' | 'close' | 'export' | 'autosave' | TimelineExitFlushOrigin) => {
      if (!canvas || isEditSpecPrimary || directorActive) {
        timelineQaLog({ event: 'timeline-persist-skipped', origin, nodeId, hasCanvas: Boolean(canvas), isEditSpecPrimary, directorActive })
        return false
      }
      const previousFingerprint = lastPersistedTimelineFingerprintRef.current
      const changed = currentTimelineConfigFingerprint !== previousFingerprint
      const realUserEditDirty = editRevision !== lastPersistedTimelineEditRevisionRef.current && changed
      const explicitExport = origin === 'export'
      if (!changed || (!realUserEditDirty && !explicitExport)) {
        timelineQaLog({
          event: 'timeline-persist-skipped',
          origin,
          nodeId,
          reason: !changed ? 'unchanged' : 'no-user-edit',
          editRevision,
          lastPersistedEditRevision: lastPersistedTimelineEditRevisionRef.current,
        })
        return false
      }
      if (shouldHoldProvisionalFallbackConfig(origin)) return false

      canvas.updateNodeData({ id: nodeId, type: 'videoEditor' }, { in_video_config: currentTimelineConfig })
      setLastPersistedTimelineFingerprint(currentTimelineConfigFingerprint)
      lastPersistedTimelineFingerprintRef.current = currentTimelineConfigFingerprint
      setLastPersistedTimelineEditRevision(editRevision)
      lastPersistedTimelineEditRevisionRef.current = editRevision
      hasUnsavedTimelineConfigRef.current = false
      const previousConfigHash = timelineStringTraceHash(previousFingerprint)
      const nextConfigHash = timelineConfigTraceHash(currentTimelineConfig)
      const sourceRefSummary = summarizeTimelineSourceRefs(currentTimelineConfig)
      const [previousConfigSizeBytes, previousConfigDigest] = previousConfigHash.split(':')
      const [nextConfigSizeBytes, nextConfigDigest] = nextConfigHash.split(':')
      const writeResult = changed ? 'updated' : 'unchanged'
      timelineQaLog({
        event: 'timeline-config-persisted',
        origin,
        nodeId,
        clipCount: timelineState.clips.length,
        audioClipCount: timelineState.audioClips.length,
        targetField: 'in_video_config',
        writeTarget: 'canvas.updateNodeData',
        configHash: nextConfigHash,
        previousConfigHash,
        nextConfigHash,
        previousConfigSizeBytes: Number(previousConfigSizeBytes),
        previousConfigDigest,
        nextConfigSizeBytes: Number(nextConfigSizeBytes),
        nextConfigDigest,
        changed,
        writeResult,
        sourceRefSummary,
      })
      emitTimelineSourceEvent({
        event: origin === 'autosave'
          ? 'timeline-modal-autosave-persisted-in-video-config'
          : 'timeline-node-data-persisted-in-video-config',
        origin,
        trigger: origin,
        source: 'timeline-editor-state',
        targetField: 'in_video_config',
        writeTarget: 'canvas.updateNodeData',
        configHash: nextConfigHash,
        previousConfigHash,
        nextConfigHash,
        previousConfigSizeBytes: Number(previousConfigSizeBytes),
        previousConfigDigest,
        nextConfigSizeBytes: Number(nextConfigSizeBytes),
        nextConfigDigest,
        changed,
        writeResult,
        sourceRefSummary,
        nodeId,
        clipCount: timelineState.clips.length,
        audioClipCount: timelineState.audioClips.length,
        trackCount: timelineState.videoTrackCount + timelineState.audioTrackCount,
        timelineItemCount: timelineState.clips.length + timelineState.audioClips.length,
        duration_ms: Math.round(timelineState.totalDuration * 1000),
      })
      return true
    },
    [
      canvas,
      currentTimelineConfig,
      currentTimelineConfigFingerprint,
      editRevision,
      isEditSpecPrimary,
      directorActive,
      nodeId,
      shouldHoldProvisionalFallbackConfig,
      timelineState,
    ],
  )

  const persistTimelineConfigRef = useRef(persistTimelineConfig)
  persistTimelineConfigRef.current = persistTimelineConfig
  const exitFlushRef = useRef<(origin: TimelineExitFlushOrigin) => void>(() => undefined)
  exitFlushRef.current = (origin) => {
    if (autosaveTimerRef.current) {
      clearTimeout(autosaveTimerRef.current)
      autosaveTimerRef.current = null
    }
    const dirty = hasUnsavedTimelineConfigRef.current
    const timelineLoaded = timelineLoadedRef.current
    const shouldFlush = shouldFlushTimelineOnExit(timelineLoaded, dirty)
    timelineQaLog({
      event: shouldFlush ? 'timeline-exit-flush-requested' : 'timeline-exit-flush-skipped',
      origin,
      nodeId,
      dirty,
      timelineLoaded,
    })
    if (shouldFlush) persistTimelineConfigRef.current(origin)
  }

  useEffect(() => bindTimelineExitFlush({
    targetWindow: window,
    targetDocument: document,
    flush: (origin) => exitFlushRef.current(origin),
  }), [])

  useEffect(() => {
    // RFC-1035 (RC4): autosave runs during render too. The fingerprint guard
    // below still prevents writing when nothing changed, so this only persists
    // genuine edits. Persisting `in_video_config` mid-render is safe — the
    // in-flight render already snapshotted its config at execute time
    // (agent.py), so a later YDoc write cannot affect the running render.
    if (!timelineLoaded || !canvas) return
    if (!hasUnsavedTimelineConfig) return

    if (autosaveTimerRef.current) {
      clearTimeout(autosaveTimerRef.current)
    }

    autosaveTimerRef.current = setTimeout(() => {
      persistTimelineConfig('autosave')
      autosaveTimerRef.current = null
    }, 2000)

    return () => {
      if (autosaveTimerRef.current) {
        clearTimeout(autosaveTimerRef.current)
        autosaveTimerRef.current = null
      }
    }
  }, [
    canvas,
    currentTimelineConfigFingerprint,
    hasUnsavedTimelineConfig,
    lastPersistedTimelineFingerprint,
    persistTimelineConfig,
    timelineLoaded,
  ])

  const handleUpdateTimelineConfig = useCallback(() => {
    if (timelineLocked) return
    const persisted = persistTimelineConfig('update-button')
    timelineQaLog({
      event: 'timeline-update-clicked',
      nodeId,
      persisted,
      dirtyBeforeClick: hasUnsavedTimelineConfig,
    })
  }, [hasUnsavedTimelineConfig, timelineLocked, nodeId, persistTimelineConfig])

  // Save timeline config to canonical node input on close.
  // RFC-1035 (RC4): always persist on close, even while a render is active.
  // Previously this was gated on `!isRenderActive`, which meant closing the
  // modal (or hitting the render overlay's "Go back to canvas") during/after an
  // export discarded any un-persisted edits. Persisting mid-render is safe: the
  // in-flight render already snapshotted its config via executeNode inputUpdates
  // and the backend snapshots inputs at execute time, so a later YDoc write to
  // in_video_config cannot affect the running render.
  const handleClose = useCallback(() => {
    if (autosaveTimerRef.current) {
      clearTimeout(autosaveTimerRef.current)
      autosaveTimerRef.current = null
    }
    if (canvas) {
      timelineLog({
        event: 'timeline-config-flushed-before-close',
        nodeId,
        clipCount: timelineState.clips.length,
        audioClipCount: timelineState.audioClips.length,
        isRenderActive,
        sourceOfTruth: 'in_video_config',
      })
      timelineLog({ event: 'save-on-close', nodeId, clipCount: timelineState.clips.length })
      persistTimelineConfig('close')
    }
    onClose()
  }, [isRenderActive, timelineState, canvas, nodeId, onClose, persistTimelineConfig])

  // Keyboard shortcuts
  const editorShellRef = useRef<HTMLDivElement>(null)
  const toggleFullscreen = useCallback(async () => {
    try {
      if (document.fullscreenElement) await document.exitFullscreen()
      else await editorShellRef.current?.requestFullscreen()
    } catch (error) { toast.error(error instanceof Error ? error.message : String(error)) }
  }, [])
  const [legacySourceFormat, setLegacySourceFormat] = useState<EditSpecDict['format']>()
  const handleSourceSize = useCallback((width: number, height: number) => {
    const aspect: EditSpecDict['format']['aspect'] = width === height ? '1:1' : width < height ? '9:16' : '16:9'
    const ratio = aspect === '1:1' ? 1 : aspect === '9:16' ? 9 / 16 : 16 / 9
    const canvasWidth = Math.abs(width / height - ratio) / ratio <= 0.01 ? width : Math.round(height * ratio / 2) * 2
    setLegacySourceFormat((current) => current?.width === canvasWidth && current?.height === height ? current : { aspect, width: canvasWidth, height, fps: LEGACY_TIMELINE_FPS })
  }, [])
  const legacyText = useLegacyTextTracks({ state: timelineState, selectedKey: selectedTextKey,
    onSelect: (key) => { clearSelection(); setSelectedTextKey(key); setRightPanelMode('config') },
    onCommit: setTextOverlays, disabled: timelineLocked, nodeId, onSeek: setPlayhead, sourceFormat: legacySourceFormat })
  const legacyPreviewText = applyTextDraft(legacyText.document, textDraft, 'legacy')
  const selectTimelineClip = useCallback((id: string | null, kind?: 'video' | 'audio' | null) => {
    setSelectedTextKey(null); setRightPanelMode('config'); selectClip(id, kind)
  }, [selectClip])
  useEffect(() => {
    const handleKeyDown = (e: KeyboardEvent) => {
      // The bailout used to skip the entire shortcut handler for any focused
      // <input>/<textarea>, which meant a focused volume slider (type="range")
      // would silently swallow Space and nudge its value instead of toggling
      // playback.
      //
      // The fix lets Space through for non-text inputs (range/checkbox/radio
      // and non-input targets), while keeping the bailout for text-entry
      // inputs (text/email/textarea/etc.) so users can type spaces in the
      // export-name dialog without toggling playback. All OTHER shortcuts
      // (Arrow scrub, Home/End jump, Delete, Ctrl/Cmd+Z, etc.) still bail
      // for ANY focused input so the input's native keyboard behaviour
      // (Arrow nudges value, Home/End jumps caret/value, etc.) is preserved.
      const target = e.target
      if (target instanceof Element && target.closest('[role="dialog"]') && target.closest('[role="dialog"]') !== editorShellRef.current) return
      if (target instanceof Element && (
        (target instanceof HTMLElement && target.isContentEditable) ||
        target.closest('[contenteditable="true"], [role="textbox"], [role="slider"], select')
      )) return
      // Render locking disables editor shortcuts, not native focus/activation.
      // In particular Tab, Enter and Space must reach the overlay's buttons.
      if (selectedTimelineEditLocked && !['Escape', 'Space', 'Home', 'End', 'ArrowLeft', 'ArrowRight', 'Minus', 'Equal', 'NumpadSubtract', 'NumpadAdd'].includes(e.code)) {
        const isButton = target instanceof Element && target.closest('button, [role="button"]')
        if (['Space', 'Minus', 'NumpadSubtract', 'Equal', 'NumpadAdd', 'KeyS', 'Delete',
          'Backspace', 'KeyZ', 'KeyC', 'KeyV', 'ArrowUp', 'ArrowDown', 'ArrowLeft',
          'ArrowRight', 'Home', 'End'].includes(e.code) && !(e.code === 'Space' && isButton)) {
          e.preventDefault()
          e.stopImmediatePropagation()
        }
        return
      }
      const isInputLike =
        target instanceof HTMLInputElement || target instanceof HTMLTextAreaElement
      const isTextInput =
        target instanceof HTMLTextAreaElement ||
        (target instanceof HTMLInputElement &&
          target.type !== 'range' &&
          target.type !== 'checkbox' &&
          target.type !== 'radio' &&
          target.type !== 'button' &&
          target.type !== 'submit')
      if (e.code === 'Space') {
        // Text inputs always win Space (typing). Everything else (range, body,
        // buttons) routes Space to the playback toggle.
        if (isTextInput) return
        if (dreamVibePreviewUnavailable) {
          e.preventDefault()
          e.stopImmediatePropagation()
          return
        }
        timelineLog({
          event: 'space-keydown',
          nodeId,
          targetTag: target instanceof HTMLElement ? target.tagName : 'unknown',
          targetType: target instanceof HTMLInputElement ? target.type : null,
          isInputLike,
          isTextInput,
          willToggle: true,
        })
      } else if (isInputLike) {
        return
      }

      // Escape: Close modal (render continues in the background)
      if (e.code === 'Escape') {
        e.preventDefault()
        e.stopImmediatePropagation()
        if (document.fullscreenElement) {
          void document.exitFullscreen()
          timelineQaLog({ event: 'fullscreen-escape-exit', nodeId })
          return
        }
        handleClose()
        return
      }

      if (
        dreamVibePreviewUnavailable &&
        ['ArrowLeft', 'ArrowRight', 'Home', 'End'].includes(e.code)
      ) {
        e.preventDefault()
        e.stopImmediatePropagation()
        return
      }

      // Space: Play/Pause. If the playhead is at (or near) the end of the
      // timeline AND we're about to play, reset to 0 first so users can
      // re-watch without manually scrubbing back. Epsilon = 0.05s.
      // SHORTCUT_ID: timeline.playPause (keyboardShortcuts.ts — Keyboard Shortcuts modal)
      if (e.code === 'Space') {
        e.preventDefault()
        e.stopImmediatePropagation()
        const playhead = timelineState.playheadPosition
        const total = playbackDuration
        if (
          !timelineState.isPlaying &&
          total > 0 &&
          playhead >= total - 0.05
        ) {
          timelineLog({
            event: 'spacebar-reset-at-end',
            nodeId,
            wasS: Number(playhead.toFixed(3)),
            totalS: Number(total.toFixed(3)),
          })
          // Reset playhead first; togglePlayPause then transitions to PLAY.
          setPlayhead(0)
          if (playheadPositionRef) {
            playheadPositionRef.current = 0
          }
        }
        handleUserTogglePlayPause()
        return
      }

      // Zoom keys: '-' / '+' / '=' (matches toolbar +/- buttons, ±10 step)
      // SHORTCUT_ID: timeline.zoomOut SHORTCUT_ID: timeline.zoomIn
      if (e.code === 'Minus' || e.code === 'NumpadSubtract') {
        e.preventDefault()
        e.stopImmediatePropagation()
        const next = Math.max(10, Math.min(200, timelineState.zoomLevel - 10))
        timelineLog({
          event: 'zoom-key',
          nodeId,
          direction: 'out',
          delta: -10,
          fromZoom: timelineState.zoomLevel,
          toZoom: next,
        })
        setZoom(next)
        return
      }
      if (e.code === 'Equal' || e.code === 'NumpadAdd') {
        e.preventDefault()
        e.stopImmediatePropagation()
        const next = Math.max(10, Math.min(200, timelineState.zoomLevel + 10))
        timelineLog({
          event: 'zoom-key',
          nodeId,
          direction: 'in',
          delta: 10,
          fromZoom: timelineState.zoomLevel,
          toZoom: next,
        })
        setZoom(next)
        return
      }

      // S: Split selected clip at playhead (type-aware)
      // SHORTCUT_ID: timeline.split
      if (
        isEditSpecPrimary &&
        (e.code === 'KeyS' ||
          e.code === 'Delete' ||
          e.code === 'Backspace' ||
          e.code === 'KeyZ' ||
          e.code === 'KeyV')
      ) {
        e.preventDefault()
        e.stopImmediatePropagation()
        return
      }
      if (e.code === 'KeyS' && !e.ctrlKey && !e.metaKey && !e.shiftKey) {
        e.preventDefault()
        e.stopImmediatePropagation()
        setPlayhead(playheadPositionRef.current)
        splitSelectedClip()
        return
      }

      // Delete/Backspace: Remove selected clips (multi-aware)
      // Shift+Delete or Shift+Backspace → Ripple delete (Premiere-style)
      // SHORTCUT_ID: timeline.delete SHORTCUT_ID: timeline.rippleDelete
      if ((e.code === 'Delete' || e.code === 'Backspace') && !e.ctrlKey && !e.metaKey) {
        e.preventDefault()
        e.stopImmediatePropagation()
        if (selectedTextKey && !isEditSpecPrimary) { legacyText.remove(); return }
        if (e.shiftKey) {
          rippleDeleteSelected()
        } else {
          deleteSelectedClip()
        }
        return
      }

      // Ctrl/Cmd+Z: Undo — SHORTCUT_ID: timeline.undo
      if (e.code === 'KeyZ' && (e.ctrlKey || e.metaKey) && !e.shiftKey) {
        e.preventDefault()
        e.stopImmediatePropagation()
        if (canUndo) {
          undo()
        }
        return
      }

      // Ctrl/Cmd+Shift+Z: Redo — SHORTCUT_ID: timeline.redo
      if (e.code === 'KeyZ' && (e.ctrlKey || e.metaKey) && e.shiftKey) {
        e.preventDefault()
        e.stopImmediatePropagation()
        if (canRedo) {
          redo()
        }
        return
      }

      // Ctrl/Cmd+C: Copy selected clip — SHORTCUT_ID: timeline.copy
      if (e.code === 'KeyC' && (e.ctrlKey || e.metaKey) && !e.shiftKey) {
        e.preventDefault()
        e.stopImmediatePropagation()
        copyClip()
        return
      }

      // Ctrl/Cmd+V: Paste copied clip — SHORTCUT_ID: timeline.paste
      if (e.code === 'KeyV' && (e.ctrlKey || e.metaKey) && !e.shiftKey) {
        e.preventDefault()
        e.stopImmediatePropagation()
        pasteClip()
        return
      }

      // SHORTCUT_ID: timeline.shot
      if (e.code === 'ArrowUp' || e.code === 'ArrowDown') {
        e.preventDefault()
        e.stopImmediatePropagation()
        const activeClips = isEditSpecPrimary && activeEditSpec
          ? projectEditSpecTimeline(activeEditSpec, dreamVibeSourceRevisions, timelineState, editSpecSourceMetadata.durations).clips : timelineState.clips
        const boundaries = [...new Set([0, playbackDuration, ...activeClips.flatMap((clip) => [clip.startOffset, clip.startOffset + clip.effectiveDuration])])].sort((a, b) => a - b)
        const current = playheadPositionRef.current
        const next = e.code === 'ArrowUp'
          ? [...boundaries].reverse().find((time) => time < current - 0.001) ?? 0
          : boundaries.find((time) => time > current + 0.001) ?? playbackDuration
        if (timelineState.isPlaying) togglePlayPause()
        playheadPositionRef.current = next
        setPlayhead(next)
        return
      }

      // SHORTCUT_ID: timeline.scrub
      // SHORTCUT_ID: timeline.fineScrub
      if (e.code === 'ArrowLeft' || e.code === 'ArrowRight') {
        e.preventDefault()
        e.stopImmediatePropagation()
        const fps = isEditSpecPrimary && activeEditSpec ? activeEditSpec.format.fps : LEGACY_TIMELINE_FPS
        const before = playheadPositionRef.current
        const next = stepTimelineFrame(before, e.code === 'ArrowLeft' ? -1 : 1, fps, playbackDuration, e.shiftKey)
        if (timelineState.isPlaying) togglePlayPause()
        playheadPositionRef.current = next
        setPlayhead(next)
        timelineQaLog({ event: 'frame-step', nodeId, fps, frame: Math.round(next * fps), before, after: next, bySecond: e.shiftKey })
        return
      }
      // SHORTCUT_ID: timeline.fullscreen
      if (e.code === 'KeyF' && !e.ctrlKey && !e.metaKey) {
        e.preventDefault()
        e.stopImmediatePropagation()
        void toggleFullscreen()
        return
      }

      // Home: Jump to start — SHORTCUT_ID: timeline.jumpStart
      if (e.code === 'Home') {
        e.preventDefault()
        e.stopImmediatePropagation()
        jumpToStart()
        return
      }

      // End: Jump to end — SHORTCUT_ID: timeline.jumpEnd
      if (e.code === 'End') {
        e.preventDefault()
        e.stopImmediatePropagation()
        jumpToEnd()
        return
      }
    }

    // Use capture phase on document so we fire BEFORE ReactFlow's bubble-phase
    // keydown listener on document. This prevents Delete/Backspace from deleting
    // the canvas node while the timeline editor modal is open.
    document.addEventListener('keydown', handleKeyDown, true)
    return () => document.removeEventListener('keydown', handleKeyDown, true)
  }, [
    selectedTextKey, legacyText, toggleFullscreen, activeEditSpec, dreamVibeSourceRevisions, editSpecSourceMetadata.durations, togglePlayPause,
    handleUserTogglePlayPause,
    splitSelectedClip,
    deleteSelectedClip,
    rippleDeleteSelected,
    canUndo,
    canRedo,
    undo,
    redo,
    copyClip,
    pasteClip,
    jumpToStart,
    jumpToEnd,
    handleClose,
    nodeId,
    setPlayhead,
    setZoom,
    timelineState,
    playbackDuration,
    playheadPositionRef,
    isEditSpecPrimary,
    timelineLocked,
    selectedTimelineEditLocked,
    dreamVibePreviewUnavailable,
  ])

  // Open the Export confirm sheet (replaces direct render dispatch).
  const handleOpenExportSheet = useCallback(() => {
    if (!projectId) return
    if (!hasPlaybackClips) return
    if (timelineLocked) return
    if (isEditSpecPrimary && !dreamVibeRenderEnabled) return
    if (sourceBlockReason) {
      exportFlowLog({ event: 'export-blocked-source', origin: 'timeline-modal', nodeId })
      toast.error(sourceBlockReason)
      return
    }
    if (exportDurationTooShort) {
      exportFlowLog({
        event: 'export-blocked-minimum-duration',
        origin: 'timeline-modal',
        nodeId,
        durationSeconds: playbackDuration,
      })
      toast.error(minimumExportDurationMessage)
      return
    }
    const defaultName = buildDefaultExportName()
    setExportSheetDefaultName(defaultName)
    setIsExportSheetOpen(true)
    exportFlowLog({
      event: 'open-export-sheet',
      origin: 'timeline-modal',
      nodeId,
      defaultName,
      existingLabelsCount: existingLabels.size,
    })
  }, [
    dreamVibeRenderEnabled,
    existingLabels,
    isEditSpecPrimary,
    timelineLocked,
    hasPlaybackClips,
    exportDurationTooShort,
    minimumExportDurationMessage,
    nodeId,
    playbackDuration,
    projectId,
    sourceBlockReason,
  ])

  // Cancel the confirm sheet.
  const handleCancelExportSheet = useCallback(() => {
    setIsExportSheetOpen(false)
    exportFlowLog({ event: 'cancel-export-sheet', origin: 'timeline-modal', nodeId })
  }, [nodeId])

  // Confirm sheet → write the chosen name and dispatch the render.
  const handleConfirmExport = useCallback(
    async (chosenName: string) => {
      if (!projectId) return
      if (!hasPlaybackClips) return
      if (isEditSpecPrimary && !dreamVibeRenderEnabled) return
      if (sourceBlockReason) {
        exportFlowLog({ event: 'export-blocked-source', origin: 'timeline-modal-confirm', nodeId })
        toast.error(sourceBlockReason)
        setIsExportSheetOpen(false)
        return
      }
      if (exportDurationTooShort) {
        exportFlowLog({
          event: 'export-blocked-minimum-duration',
          origin: 'timeline-modal-confirm',
          nodeId,
          durationSeconds: playbackDuration,
        })
        toast.error(minimumExportDurationMessage)
        setIsExportSheetOpen(false)
        return
      }
      if (timelineLocked) {
        setIsExportSheetOpen(false)
        return
      }
      setIsExportSheetOpen(false)

      flushSync(() => {
        setIsRendering(true)
        setRenderProgress(0)
        setRenderMessage(t('timelineEditorModal.renderingInBackground'))
      })

      try {
        if (isEditSpecPrimary && activeEditSpec === null) {
          throw new Error('editspec_primary export requires an active EditSpec')
        }
        const renderEditSpec = isEditSpecPrimary ? activeEditSpec : null
        const renderConfig = renderEditSpec === null ? currentTimelineConfig : null
        timelineLog({
          event: 'timeline-config-flushed-before-export',
          nodeId,
          clipCount: timelineState.clips.length,
          audioClipCount: timelineState.audioClips.length,
          sourceOfTruth: renderEditSpec === null ? 'in_video_config' : 'in_edit_spec',
        })

        const renderLogPayload = {
          event: 'render-requested',
          nodeId,
          chosenName,
          clipCount: timelineState.clips.length,
          audioClipCount: timelineState.audioClips.length,
          totalDuration: timelineState.totalDuration,
          videoClips: timelineState.clips.map((clip) => ({
            clipId: clip.id,
            sourcePath: clip.sourcePath,
            startTimeS: Number(clip.trimStart.toFixed(3)),
            endTimeS: Number((clip.originalDuration - clip.trimEnd).toFixed(3)),
            startOffsetS: Number(clip.startOffset.toFixed(3)),
            speed: Number(clip.speed.toFixed(3)),
            trackIndex: clip.trackIndex,
            volume: Number(clip.volume.toFixed(3)),
            muted: clip.muted,
          })),
          audioTracks: timelineState.audioClips
            .filter((clip) => !clip.muted)
            .map((clip) => ({
              clipId: clip.id,
              startTimeS: Number(clip.trimStart.toFixed(3)),
              endTimeS: Number((clip.originalDuration - clip.trimEnd).toFixed(3)),
              startOffsetS: Number(clip.startOffset.toFixed(3)),
              speed: Number(clip.speed.toFixed(3)),
              volume: Number(clip.volume.toFixed(3)),
            })),
        }
        timelineLog(renderLogPayload)
        emitTimelineNadEvent('dw:timeline-playback:render-contract', renderLogPayload)
        exportFlowLog({ event: 'confirm-export', origin: 'timeline-modal', nodeId, chosenName })

        // Legacy timelines persist their editor state before execution. Primary
        // EditSpecs are already canonical and must never be overwritten by the
        // lossy legacy timeline adapter.
        const holdProvisionalTimelineConfig =
          renderEditSpec === null && shouldHoldProvisionalFallbackConfig('export')
        if (renderEditSpec === null && !holdProvisionalTimelineConfig) {
          persistTimelineConfig('export')
        }
        canvas?.updateNodeData({ id: nodeId, type: 'videoEditor' }, {
          in_export_name: chosenName,
          progressMessage: t('timelineEditorModal.renderStarted'),
        })
        timelineLog({
          event: 'execute-with-fresh-inputs',
          nodeId,
          hasInputUpdates: true,
          clipCount: timelineState.clips.length,
          audioClipCount: timelineState.audioClips.length,
        })
        await executeNode(projectId, nodeId, renderEditSpec === null
          ? {
              action: 'timeline_render',
              inputUpdates: holdProvisionalTimelineConfig
                ? { in_export_name: chosenName }
                : {
                    in_video_config: renderConfig,
                    in_export_name: chosenName,
                  },
              canvasId,
            }
          : {
              action: 'vibe_render',
              inputUpdates: {
                in_export_name: chosenName,
              },
              canvasId,
            })

        // executeNode returns immediately. Backend updates node status via Yjs.
        setRenderMessage(t('timelineEditorModal.renderStarted'))
      } catch (err) {
        console.error('Render failed:', err)
        setIsRendering(false)
        setRenderProgress(0)
        setRenderMessage(t('timelineEditorModal.renderFailed'))
      }
    },
    [projectId, nodeId, timelineState, currentTimelineConfig, activeEditSpec, isEditSpecPrimary, canvas, timelineLocked, hasPlaybackClips, t, canvasId, persistTimelineConfig, shouldHoldProvisionalFallbackConfig, dreamVibeRenderEnabled, exportDurationTooShort, minimumExportDurationMessage, playbackDuration, sourceBlockReason],
  )

  // Cancel ongoing render.
  //
  // On success we close the overlay OPTIMISTICALLY (clearing
  // ``isRendering`` and ``renderMessage`` immediately) rather than
  // waiting for the YDoc status round-trip to flip the node out of
  // ``running``. That sub-100ms feedback is the happy-path UX; the
  // terminal-status effect above is the safety net for dropped
  // responses or crash-recovery reopens.
  //
  // On rejection we leave ``isRendering``/``renderMessage`` alone so
  // the overlay stays mounted and the user can retry — only the
  // ``isCancelling`` flag in ``finally`` is reset so the Cancel button
  // re-enables.
  const handleCancelRender = useCallback(async () => {
    if (!projectId || isCancelling) return
    cancelDebug('cancel_requested nodeId=%s', nodeId)
    timelineLog({ event: 'modal-cancel-requested', nodeId })
    trackGenerationCancelled({ projectId, nodeId, nodeType: 'video_editor', stage: 'running' })
    setIsCancelling(true)
    try {
      await cancelNodeExecution(projectId, nodeId, canvasId)
      cancelDebug('cancel_success_optimistic_close nodeId=%s', nodeId)
      // Optimistic close: tear the overlay down before the YDoc
      // status mirror catches up.
      setIsRendering(false)
      setRenderMessage('')
      setRenderProgress(0)
      timelineLog({ event: 'modal-cancel-dispatched', nodeId })
    } catch (err) {
      cancelDebug('cancel_failed nodeId=%s error=%s', nodeId, String(err))
      timelineLog({ event: 'modal-cancel-failed', nodeId, error: String(err) })
      console.error('Cancel render failed:', err)
    } finally {
      setIsCancelling(false)
    }
  }, [projectId, nodeId, isCancelling, canvasId])

  // RFC-0651 #3: when magnet is on, schedule a track-0 pack on the next
  // microtask after any commit that may have affected track 0. We use
  // requestAnimationFrame so the reducer's history push completes first.
  const magnetEnabledRef = useRef(timelineState.magnetEnabled)
  magnetEnabledRef.current = timelineState.magnetEnabled
  const schedulePack = useCallback(() => {
    if (!magnetEnabledRef.current) return
    requestAnimationFrame(() => {
      if (!magnetEnabledRef.current) return
      magnetPackTrack0()
    })
  }, [magnetPackTrack0])

  const handleTrimClipCommit = useCallback(
    (clipId: string, trimStart: number, trimEnd: number, startOffset?: number) => {
      trimClip(clipId, trimStart, trimEnd, startOffset)
      trackTimelineEdit(projectId, nodeId, 'trim', 'video')
      const target = timelineStateRef.current.clips.find((c) => c.id === clipId)
      if (target?.trackIndex === 0) schedulePack()
    },
    [trimClip, schedulePack, projectId, nodeId],
  )

  const handleMultiDragCommit = useCallback(
    (deltaSec: number, trackDelta?: number) => {
      multiDragOffset(deltaSec, trackDelta)
      const touchesTrack0 = Array.from(timelineStateRef.current.selectedClipIds).some(
        (id) => timelineStateRef.current.clips.find((c) => c.id === id)?.trackIndex === 0,
      )
      if (touchesTrack0) schedulePack()
    },
    [multiDragOffset, schedulePack],
  )

  const handleSetClipOffsetCommit = useCallback(
    (clipId: string, startOffset: number) => {
      setClipOffset(clipId, startOffset)
      const target = timelineStateRef.current.clips.find((c) => c.id === clipId)
      if (target?.trackIndex === 0) schedulePack()
    },
    [setClipOffset, schedulePack],
  )

  const handleSplitSelected = useCallback(() => {
    setPlayhead(playheadPositionRef.current)
    splitSelectedClip()
    trackTimelineEdit(projectId, nodeId, 'split')
    schedulePack()
  }, [setPlayhead, playheadPositionRef, splitSelectedClip, schedulePack, projectId, nodeId])
  const handleCutAtPlayhead = useCallback((side: 'left' | 'right') => {
    setPlayhead(playheadPositionRef.current)
    cutAtPlayhead(side)
    trackTimelineEdit(projectId, nodeId, side === 'left' ? 'cut_left' : 'cut_right')
  }, [setPlayhead, playheadPositionRef, cutAtPlayhead, projectId, nodeId])

  // RFC-0630 v3 (architect-review #2): reorder release is one of the six
  // commit points the plan listed for magnet-pack. Reorder may move a clip
  // between tracks; pack if either the source's *old* trackIndex was 0 OR
  // the clip after the move sits on track 0. The reorder reducer doesn't
  // change trackIndex, so checking the current trackIndex is sufficient.
  const handleReorderClipCommit = useCallback(
    (clipId: string, newOrder: number) => {
      reorderClip(clipId, newOrder)
      trackTimelineEdit(projectId, nodeId, 'reorder', 'video')
      const target = timelineStateRef.current.clips.find((c) => c.id === clipId)
      if (target?.trackIndex === 0) schedulePack()
    },
    [reorderClip, schedulePack, projectId, nodeId],
  )

  // RFC-0651 #5: resizable panel dividers (left-rail/preview,
  // preview/right-rail, preview/timeline). Sizes persist to localStorage
  // under `videoEditor:panel-sizes`. Clamps:
  //   leftWidth     180–400 px
  //   rightWidth    200–400 px
  //   timelineHeight 200–500 px
  const PANEL_SIZES_LS_KEY = 'videoEditor:workspace-panel-sizes'
  // Module-level constants (not state) so they don't need to be deps of
  // the splitter handler — `useCallback` only watches the dynamic inputs.
  const PANEL_DEFAULTS = { left: 340, right: 370, timeline: 280 }
  const layoutLog = useMemo(() => createNadDebug('dw:video-editor:layout'), [])
  const [panelSizes, setPanelSizes] = useState(() => {
    try {
      const raw = localStorage.getItem(PANEL_SIZES_LS_KEY)
      if (raw) {
        const parsed = JSON.parse(raw)
        const clamp = (val: number, range: { min: number; max: number }) =>
          Math.max(range.min, Math.min(range.max, val))
        return {
          left: clamp(
            typeof parsed.left === 'number' ? parsed.left : PANEL_DEFAULTS.left,
            PANEL_CLAMPS.left,
          ),
          right: clamp(
            typeof parsed.right === 'number' ? parsed.right : PANEL_DEFAULTS.right,
            PANEL_CLAMPS.right,
          ),
          timeline: clamp(
            typeof parsed.timeline === 'number' ? parsed.timeline : PANEL_DEFAULTS.timeline,
            PANEL_CLAMPS.timeline,
          ),
        }
      }
    } catch (err) {
      console.warn('[videoEditor] failed to read panel sizes from localStorage', err)
    }
    return PANEL_DEFAULTS
  })

  // Persist panel sizes (debounced via a microtask).
  useEffect(() => {
    try {
      localStorage.setItem(PANEL_SIZES_LS_KEY, JSON.stringify(panelSizes))
    } catch (err) {
      console.warn('[videoEditor] failed to persist panel sizes', err)
    }
  }, [panelSizes])

  // Splitter drag handler factory. `dimension` selects which size we adjust
  // and `axis` selects whether we read deltaX or deltaY. We track the start
  // position + initial size in refs so React batching doesn't lose state.
  const startSplitterDrag = useCallback(
    (
      e: React.PointerEvent<HTMLDivElement>,
      dimension: 'left' | 'right' | 'timeline',
      axis: 'x' | 'y',
      direction: 1 | -1,
    ) => {
      e.preventDefault()
      e.stopPropagation()
      const startCoord = axis === 'x' ? e.clientX : e.clientY
      const startSize = panelSizes[dimension]
      const clamp = PANEL_CLAMPS[dimension]
      const handleMove = (event: PointerEvent) => {
        const coord = axis === 'x' ? event.clientX : event.clientY
        const delta = (coord - startCoord) * direction
        const next = Math.max(clamp.min, Math.min(clamp.max, startSize + delta))
        setPanelSizes((prev) => (prev[dimension] === next ? prev : { ...prev, [dimension]: next }))
      }
      const handleUp = () => {
        document.removeEventListener('pointermove', handleMove)
        document.removeEventListener('pointerup', handleUp)
        layoutLog({
          event: 'divider-released',
          divider: dimension,
          startSize,
          // setPanelSizes is async — we log via a microtask to capture the
          // committed value.
        })
      }
      document.addEventListener('pointermove', handleMove)
      document.addEventListener('pointerup', handleUp)
      layoutLog({ event: 'divider-grabbed', divider: dimension, axis, startSize })
    },
    [panelSizes, layoutLog],
  )

  return createPortal(
    <div
      className="fixed inset-0 z-[9999] bg-background text-foreground"
      data-testid="timeline-modal-backdrop"
      // Backdrop dismissal removed: timeline editor is a dedicated workspace.
      // Use the X button or Escape to close.
      onClick={(e) => {
        if (e.target === e.currentTarget) {
          timelineLog({
            event: 'outside-click-blocked',
            nodeId,
            isRenderActive,
          })
        }
      }}
      onKeyDown={(e) => e.stopPropagation()}
    >
      <div
        ref={editorShellRef}
        className="relative h-full w-full bg-background overflow-hidden"
        role="dialog"
        aria-modal="true"
        aria-label={t('timelineEditorModal.timelineEditor')}
        data-testid="timeline-modal-shell"
        style={{
          display: 'grid',
          // RFC-0651 #5: state-backed sizes with drag-resizable dividers.
          // Columns: [left rail] [splitter] [preview] [splitter] [right rail]
          // Rows:    [toolbar] [main] [splitter] [timeline]
          gridTemplateColumns: `minmax(240px, ${panelSizes.left}px) 4px minmax(0, 1fr) 4px minmax(200px, ${panelSizes.right}px)`,
          gridTemplateRows: `52px minmax(0, 1fr) 4px min(${panelSizes.timeline}px, 42vh)`,
        }}
        onClick={(e) => e.stopPropagation()}
      >
        {/* Row 1: Modal Header (spans all 3 columns) */}
        <div
          className="flex items-center justify-between px-4 border-b border-border bg-surface-0"
          style={{ gridColumn: '1 / -1' }}
        >
          <div className="flex items-center gap-3">
            <button type="button" onClick={handleClose} aria-label={t('timelineOverhaul.backToCanvas')}
              className="flex h-7 w-7 items-center justify-center rounded-lg border border-input-border bg-surface-1 hover:bg-surface-3 hover:border-input-border-hover focus-visible:ring-2 focus-visible:ring-primary">
              <ArrowLeft className="h-4 w-4" />
            </button>
            <h2 className="text-base font-semibold">{t('timelineEditorModal.timelineEditor')}</h2>
          </div>
          <div className="flex items-center gap-3">
            <div
              className="flex items-center gap-2"
              data-testid="timeline-modal-save-controls"
            >
              <span
                className={`h-7 rounded-full border px-3 text-xs font-medium flex items-center ${
                  hasUnsavedTimelineConfig || editSpecSaving
                    ? 'border-warning/40 bg-warning/10 text-warning'
                    : 'border-success/35 bg-success/10 text-success'
                }`}
                data-testid="timeline-modal-save-status"
              >
                {editSpecSaving ? t('timelineDirector.saving') : hasUnsavedTimelineConfig ? t('timelineEditorModal.draft') : t('timelineEditorModal.saved')}
              </span>
              <button
                onClick={handleUpdateTimelineConfig}
                disabled={!hasUnsavedTimelineConfig || timelineLocked || provisionalFallbackConfigHeld}
                className="h-7 rounded-lg border border-input-border bg-surface-1 px-3 text-xs font-medium text-foreground transition-colors hover:bg-surface-3 hover:border-input-border-hover disabled:opacity-40 disabled:cursor-not-allowed disabled:hover:bg-surface-1 disabled:hover:border-input-border"
                data-testid="timeline-modal-update-button"
                title={hasUnsavedTimelineConfig ? t('timelineEditorModal.saveTimelineEdits') : t('timelineEditorModal.timelineEditsSaved')}
              >
                {t('timelineEditorModal.update')}
              </button>
            </div>
            {/* Export Button */}
            <button
              onClick={handleOpenExportSheet}
              disabled={timelineLocked || dreamVibeRenderUnavailable || Boolean(sourceBlockReason)}
              title={sourceBlockReason ?? (dreamVibeRenderUnavailable
                ? t('timelineEditorModal.dreamVibeExportUnavailable')
                : exportDurationTooShort
                  ? minimumExportDurationMessage
                  : undefined)}
              className={`h-9 px-4 rounded-lg text-sm font-semibold transition-colors flex items-center gap-2 ${
                isRenderActive
                  ? 'bg-warning/20 text-warning cursor-wait'
                  : dreamVibeRenderUnavailable || sourceBlockReason
                    ? 'bg-surface-1 border border-input-border text-text-muted cursor-not-allowed opacity-60'
                  : 'border border-primary bg-primary hover:bg-primary/90 text-white'
              }`}
              data-testid="timeline-modal-export-button"
            >
              {isRenderActive ? (
                <>
                  <div className="w-4 h-4 border-2 border-current border-t-transparent rounded-full animate-spin" />
                  {t('timelineEditorModal.rendering')}
                </>
              ) : (
                <>
                  <svg className="w-4 h-4" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                    <path
                      strokeLinecap="round"
                      strokeLinejoin="round"
                      strokeWidth={2}
                      d="M14.752 11.168l-3.197-2.132A1 1 0 0010 9.87v4.263a1 1 0 001.555.832l3.197-2.132a1 1 0 000-1.664z"
                    />
                    <path
                      strokeLinecap="round"
                      strokeLinejoin="round"
                      strokeWidth={2}
                      d="M21 12a9 9 0 11-18 0 9 9 0 0118 0z"
                    />
                  </svg>
                  {t('timelineOverhaul.export')}
                </>
              )}
            </button>

            {/* Close Button */}
            <button
              onClick={handleClose}
              data-testid="timeline-modal-close-button"
              className={`p-2 rounded-lg transition-colors ${
                'text-text-muted hover:text-foreground hover:bg-surface-2'
              }`}
            >
              <svg className="w-5 h-5" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M6 18L18 6M6 6l12 12" />
              </svg>
            </button>
          </div>
        </div>

        <aside className="flex min-h-0 flex-col overflow-hidden border-r border-border bg-surface-0"
          style={{ gridColumn: 1, gridRow: '2 / 5' }} data-testid="timeline-editor-left-panel">
          <div className="flex h-11 shrink-0 items-center gap-2 border-b border-border px-3 text-sm font-medium"><MessageSquare className="h-4 w-4" />{t('timelineOverhaul.chat')}</div>
          <div className="min-h-0 flex-1" data-testid="timeline-editor-chat">
            {projectId && <Suspense fallback={<div className="h-full bg-background" aria-busy="true" />}>
              <ScopedAgentChat projectId={projectId} canvasId={canvasId} canvasNodes={allCanvasNodes}
                {...canvas?.executionActivity} onDirectorControlChange={setDirectorControl}
                scopedWelcome={t('timelineOverhaul.chatWelcome')}
                forcedReferencedNodeIds={[nodeId]} className="h-full bg-surface-0" />
            </Suspense>}
          </div>
        </aside>

        {/* Row 2, Col 2: vertical splitter between left rail and preview */}
        <div
          className="bg-border hover:bg-primary/60 transition-colors"
          style={{ cursor: 'col-resize', gridColumn: 2, gridRow: '2 / 5' }}
          onPointerDown={(e) => startSplitterDrag(e, 'left', 'x', 1)}
          data-testid="timeline-modal-splitter-left"
          role="separator"
          aria-orientation="vertical"
          aria-label={t('timelineEditorModal.resizeLeftRail')}
        />

        {/* Row 2, Col 3: VideoPreviewPlayer (center) */}
        <div className="flex min-w-0 flex-col bg-background p-4 min-h-0" style={{ gridColumn: 3, gridRow: 2 }}>
          <div className="flex-1 min-h-0">
            {useDreamVibePreview && activeEditSpec ? (
              <Suspense fallback={<div className="h-full bg-surface-0" />}>
                <DreamVibePreview
                  ref={videoPreviewRef}
                  editSpec={previewTextSpec ?? activeEditSpec}
                  selectedTextKey={selectedTextKey} onSelectText={(key) => { setSelectedTextKey(key); setRightPanelMode('config') }} revision={activeEditSpecRevision}
                  textEditingDisabled={timelineLocked || timelineState.isPlaying}
                  onTextDraft={(key, patch, revision) => setTextDraft(patch ? { source: 'editspec', key, patch, revision } : null)}
                  onTextGestureStart={() => textEditorRef.current?.beginTextGesture()}
                  onTextCommit={(key, patch, revision) => { setTextDraft(null); textEditorRef.current?.finishTextGesture(key, patch, revision) }}
                  renderedSpecHash={renderedSpecHash}
                  resolvedSources={dreamVibeSources}
                  sourceRevisions={dreamVibeSourceRevisions}
                  playheadPosition={timelineState.playheadPosition}
                  isPlaying={timelineState.isPlaying}
                  onVideoEnd={() => togglePlayPause()}
                />
              </Suspense>
            ) : dreamVibePreviewUnavailable ? (
              <div
                className="flex h-full items-center justify-center bg-surface-0 px-6 text-center text-sm text-muted-foreground"
                data-testid="dreamvibe-preview-unavailable"
              >
                {t('timelineEditorModal.dreamVibePreviewUnavailable')}
              </div>
            ) : (
              <VideoPreviewPlayer
                onSourceSize={handleSourceSize}
                textAspect={timelineState.textOverlays?.length ? legacyText.document.format.width / legacyText.document.format.height : undefined}
                textLayer={timelineState.textOverlays?.length ? <LegacyTextPreview document={legacyPreviewText}
                  playhead={timelineState.playheadPosition} positionRef={playheadPositionRef} duration={playbackDuration}
                  selectedKey={selectedTextKey} disabled={timelineLocked || timelineState.isPlaying}
                  onSelect={(key) => { setSelectedTextKey(key); setRightPanelMode('config') }}
                  onGestureStart={legacyText.begin}
                  onDraft={(key, patch, revision) => setTextDraft(patch ? { source: 'legacy', key, patch, revision } : null)}
                  onCommit={(key, patch) => { setTextDraft(null); legacyText.finish(key, patch) }} /> : undefined}
                surface="editor"
                ownerNodeId={nodeId}
                ref={videoPreviewRef}
                clips={timelineState.clips}
                playheadPosition={timelineState.playheadPosition}
                playheadPositionRef={playheadPositionRef}
                isPlaying={timelineState.isPlaying}
                onVideoEnd={() => togglePlayPause()}
                preloader={preloader}
                mutedVideoTracks={timelineState.mutedVideoTracks}
              />
            )}
          </div>
          {/* Playback Controls */}
          <div className="flex-shrink-0 flex items-center justify-center gap-4 mt-2">
            <output className="text-xs font-mono text-muted-foreground" data-testid="timeline-frame-position" data-frame={Math.round(timelineState.playheadPosition * (isEditSpecPrimary && activeEditSpec ? activeEditSpec.format.fps : LEGACY_TIMELINE_FPS))}>
              {t('editorWorkspace.framePosition', { frame: Math.round(timelineState.playheadPosition * (isEditSpecPrimary && activeEditSpec ? activeEditSpec.format.fps : LEGACY_TIMELINE_FPS)), fps: isEditSpecPrimary && activeEditSpec ? activeEditSpec.format.fps : LEGACY_TIMELINE_FPS })}
            </output>
            <button type="button" onClick={() => void toggleFullscreen()} title={t('keyboardShortcuts.timeline.fullscreen')} className="rounded-lg border border-input-border bg-surface-1 p-2 hover:bg-surface-3 hover:border-input-border-hover focus-visible:ring-2 focus-visible:ring-primary"><Maximize className="h-4 w-4" /></button>
            <button
              onClick={jumpToStart}
              disabled={dreamVibePreviewUnavailable || timelineState.playheadPosition === 0}
              className="p-2 rounded-lg bg-surface-1 hover:bg-surface-2 text-text-secondary hover:text-foreground transition-colors disabled:opacity-30 disabled:cursor-not-allowed"
              title={t('timelineEditorModal.jumpToStart')}
            >
              <svg className="w-5 h-5" fill="currentColor" viewBox="0 0 24 24">
                <path d="M6 6h2v12H6zm3.5 6l8.5 6V6z" />
              </svg>
            </button>
            <button
              onClick={handleUserTogglePlayPause}
              disabled={dreamVibePreviewUnavailable || !hasPlaybackClips}
              className="p-3 rounded-lg bg-surface-2 hover:bg-white/15 text-foreground transition-colors disabled:opacity-30 disabled:cursor-not-allowed"
              title={t('timelineEditorModal.playPause')}
              data-testid="timeline-play-pause-button"
            >
              {timelineState.isPlaying ? (
                <svg className="w-6 h-6" fill="currentColor" viewBox="0 0 24 24">
                  <path d="M6 4h4v16H6V4zm8 0h4v16h-4V4z" />
                </svg>
              ) : (
                <svg className="w-6 h-6" fill="currentColor" viewBox="0 0 24 24">
                  <path d="M8 5v14l11-7z" />
                </svg>
              )}
            </button>
            <button
              onClick={jumpToEnd}
              disabled={dreamVibePreviewUnavailable || timelineState.playheadPosition >= playbackDuration}
              className="p-2 rounded-lg bg-surface-1 hover:bg-surface-2 text-text-secondary hover:text-foreground transition-colors disabled:opacity-30 disabled:cursor-not-allowed"
              title={t('timelineEditorModal.jumpToEnd')}
            >
              <svg className="w-5 h-5" fill="currentColor" viewBox="0 0 24 24">
                <path d="M6 18l8.5-6L6 6v12zM16 6v12h2V6h-2z" />
              </svg>
            </button>
          </div>
        </div>

        {/* Row 2, Col 4: vertical splitter between preview and right rail */}
        <div
          className="bg-border hover:bg-primary/60 transition-colors"
          style={{ cursor: 'col-resize' }}
          onPointerDown={(e) => startSplitterDrag(e, 'right', 'x', -1)}
          data-testid="timeline-modal-splitter-right"
          role="separator"
          aria-orientation="vertical"
          aria-label={t('timelineEditorModal.resizeRightRail')}
        />

        {/* Row 2, Col 5: contextual controls / scoped DreamVibe chat */}
        <div className="flex min-h-0 flex-col overflow-hidden border-l border-border">
          <div role="tablist" aria-label={t('timelineOverhaul.editorPanel')} className="flex shrink-0 gap-3 border-b border-border-subtle px-3">
            {(['all', 'imported', 'config'] satisfies ('all' | 'imported' | 'config')[]).map((tab) => {
              const Icon = tab === 'all' ? FolderOpen : tab === 'imported' ? Files : SlidersHorizontal
              const isActive = rightPanelMode === tab
              return <button key={tab} type="button" role="tab" id={`editor-${tab}-tab`}
                aria-selected={isActive} aria-controls={`editor-${tab}-panel`} data-testid={`timeline-${tab}-tab`}
                onClick={() => { setRightPanelMode(tab); timelineQaLog({ event: 'editor-panel-tab-selected', nodeId, tab }) }}
                className={`relative flex h-9 flex-1 items-center justify-center gap-1.5 px-1 text-xs font-medium transition-colors duration-200 hover:bg-surface-2 focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-primary ${isActive ? 'text-primary' : 'text-muted-foreground hover:text-foreground'}`}>
                <Icon aria-hidden="true" className="h-4 w-4 shrink-0" strokeWidth={2} />
                <span className="whitespace-nowrap">{t(`editorWorkspace.${tab}`)}</span>
                {isActive && <span aria-hidden="true" data-testid={`timeline-${tab}-underline`} className="absolute inset-x-0 -bottom-px h-0.5 rounded-full bg-primary" />}
              </button>
            })}
          </div>
          <div className="min-h-0 flex-1" hidden={rightPanelMode !== 'all'} role="tabpanel" id="editor-all-panel" aria-labelledby="editor-all-tab">
            <AllMediaPanel nodes={allCanvasNodes} nodeId={nodeId} imported={importedMedia} disabled={timelineLocked || mediaImportBusy || !onImportMedia}
              onAdd={(asset) => { onImportMedia?.(asset); setRightPanelMode('imported') }} />
          </div>
          <div className="min-h-0 flex-1 overflow-hidden" hidden={rightPanelMode !== 'imported'} role="tabpanel" id="editor-imported-panel" aria-labelledby="editor-imported-tab">
          <ClipPanel
            clips={importedTimeline.clips}
            audioClips={importedTimeline.audioClips}
            referenceDragDisabled={isEditSpecPrimary}
            referenceVideoClips={videoClips}
            referenceAudioClips={audioClipRefs}
            selectedClipIds={timelineState.selectedClipIds}
            onSelectClip={selectTimelineClip}
            onScrollToClip={handleScrollToClip}
            onAddClipToTimeline={handleAddClipToTimeline}
            onOpenReferenceSelector={onOpenReferenceSelector}
            referencesDisabled={timelineLocked || mediaImportBusy}
            referencesDisabledReason={t('videoEditorNode.referencesCannotBeChangedWhileRendering')}
          />
          </div>
          <div className="min-h-0 flex-1 overflow-y-auto" hidden={rightPanelMode !== 'config'} role="tabpanel" id="editor-config-panel" aria-labelledby="editor-config-tab">
          {!isEditSpecPrimary ? (
            legacyText.inspector ?? <fieldset disabled={selectedTimelineEditLocked}><AdjustPanel
            selectedVideoClip={selectedVideoClip}
            selectedAudioClip={selectedAudioClip}
            selectedClipType={selectedClipTypeForControls}
            multiSelectionCount={timelineState.selectedClipIds.size}
            onSpeedChange={handleSpeedChange}
            onClipVisualsChange={setClipVisuals}
            onTimeEdit={handleTimeEdit}
            onSpeedCurve={handleSpeedCurve}
            onAudioFadesChange={setAudioFades}
            timeEditBusy={timeEditBusy}
            timeEditError={timeEditError}
            playheadPosition={timelineState.playheadPosition}
            onAudioVolumeChange={setAudioVolume}
            onAudioToggleMute={toggleAudioMute}
            onClipVolumeChange={setClipVolume}
            onClipToggleMute={toggleClipMute}
            onClipFadeOutChange={setClipFadeOut}
            onClipFadeInChange={setClipFadeIn}
            onDownloadAudioClip={handleDownloadAudioClip}
            onExtractAudio={handleExtractNativeAudio}
            extractAudioDisabled={selectedVideoHasExtractedAudio}
            onChangeVoice={(voice) => { trackTimelineEdit(projectId, nodeId, 'change_voice', 'audio'); return runSelectedAudioAction('change_voice', voice) }}
            onIsolateAudio={() => { trackTimelineEdit(projectId, nodeId, 'isolate_audio', 'audio'); return runSelectedAudioAction('isolate_audio') }}
            onSplitDialogue={selectedSplitDialogueAvailable ? () => runSelectedAudioAction('split_dialogue') : undefined}
            audioActionsBusy={audioActionBusy || pendingAudioAction !== null}
            audioActionInFlight={pendingAudioAction?.actionId ?? runningAudioAction?.actionId ?? null}
            audioActionError={selectedClipAudioActionError}
            /></fieldset>
          ) : <div ref={setEditSpecInspector} className="h-full bg-background" />}
          </div>
        </div>

        {/* Row 3: horizontal splitter between preview area and timeline */}
        <div
          className="bg-border hover:bg-primary/60 transition-colors"
          style={{ cursor: 'row-resize', gridColumn: '3 / -1', gridRow: 3 }}
          onPointerDown={(e) => startSplitterDrag(e, 'timeline', 'y', -1)}
          data-testid="timeline-modal-splitter-timeline"
          role="separator"
          aria-orientation="horizontal"
          aria-label={t('timelineEditorModal.resizeTimeline')}
        />

        {/* Row 4: TimelineEditor (bottom, spans all 5 columns) */}
        <div
          className="flex min-h-0 flex-col border-t border-border"
          style={{ gridColumn: '3 / -1', gridRow: 4 }}
        >
          {isEditSpecPrimary && activeEditSpec && projectId && <div className="min-h-0 flex-1">
            {editSpecSourceMetadata.errors.length > 0 && <div role="alert" className="p-2 text-sm text-destructive">{editSpecSourceMetadata.errors.join('; ')}</div>}
            <EditSpecTimelineEditor ref={textEditorRef} selectedTextKey={selectedTextKey} onSelectText={(key) => { setSelectedTextKey(key); setRightPanelMode('config') }} sourceDurations={editSpecSourceMetadata.durations} projectId={projectId} canvasId={canvasId} nodeId={nodeId}
              editSpec={activeEditSpec} revision={activeEditSpecRevision} sources={dreamVibeSourceRevisions}
              readOnly={timelineLocked} playheadPosition={timelineState.playheadPosition}
              isPlaying={timelineState.isPlaying} onSetPlayhead={setPlayhead}
              zoomLevel={timelineState.zoomLevel} onSetZoom={setZoom}
              onSpecCommitted={commitActiveEditSpec} onSavingChange={setEditSpecSaving} inspectorTarget={editSpecInspector} />
          </div>}
          {(!isEditSpecPrimary || !activeEditSpec || !projectId) && <div className="flex min-h-0 flex-1 flex-col">
            {legacyText.toolbar}
            <TimelineEditor
            leadingTracks={legacyText.tracks}
            hasTextSelection={!!selectedTextKey}
            timelineState={timelineState}
            onSelectClip={selectTimelineClip}
            onToggleClipSelection={toggleClipSelection}
            onClearSelection={clearSelection}
            onMultiDragOffset={handleMultiDragCommit}
            onRippleDelete={closeAllGaps}
            onSetPlayhead={setPlayhead}
            onSetZoom={setZoom}
            onReorderClip={handleReorderClipCommit}
            onClipOffsetChange={handleSetClipOffsetCommit}
            onClipTrackIndexChange={setClipTrackIndex}
            onClipVolumePreview={setClipVolumePreview}
            onClipVolumeCommit={commitClipVolume}
            onTrimClip={handleTrimClipCommit}
            onTrimClipPreview={trimClipPreview}
            onNormalizeTimelineOrigin={normalizeTimelineOrigin}
            onAudioOffsetChange={setAudioOffset}
            onAudioTrimClip={trimAudioClip}
            onAudioTrimClipPreview={trimAudioClipPreview}
            onAudioTrackIndexChange={setAudioTrackIndex}
            onAudioVolumePreview={setAudioVolumePreview}
            onAudioVolumeCommit={commitAudioVolume}
            onAddTrack={(type) => {
              trackTimelineEdit(projectId, nodeId, 'add_track', type === 'text' ? 'text' : type === 'video' ? 'video' : 'audio')
              timelineLog({
                event: 'track-add',
                nodeId,
                trackType: type,
                videoTrackCount: timelineState.videoTrackCount,
                audioTrackCount: timelineState.audioTrackCount,
              })
              if (type === 'text') { legacyText.add() } else if (type === 'video') {
                addVideoTrack()
              } else {
                addAudioTrack()
              }
            }}
            onRemoveTrack={(trackKind, trackIndex, deleteClips) => {
              if (trackKind === 'video') {
                removeVideoTrack(trackIndex, deleteClips)
              } else {
                removeAudioTrack(trackIndex, deleteClips)
              }
            }}
            onDropReference={handleAddReferenceAt}
            playheadPositionRef={playheadPositionRef}
            readOnly={timelineLocked || isEditSpecPrimary}
            onSplitSelectedClip={handleSplitSelected}
            onCutAtPlayhead={handleCutAtPlayhead}
            onTransitionChange={setTransition}
            onDeleteSelectedClip={() => {
              trackTimelineEdit(projectId, nodeId, 'delete', selectedTextKey ? 'text' : undefined)
              if (selectedTextKey) legacyText.remove()
              else deleteSelectedClip()
            }}
            onCopyClip={copyClip}
            onPasteClip={pasteClip}
            onUndo={undo}
            onRedo={redo}
            canUndo={canUndo}
            canRedo={canRedo}
            magnetEnabled={timelineState.magnetEnabled}
            onSetMagnetEnabled={setMagnetEnabled}
            onMagnetPackTrack0={magnetPackTrack0}
            mutedVideoTracks={timelineState.mutedVideoTracks}
            mutedAudioTracks={timelineState.mutedAudioTracks}
            onToggleTrackMute={toggleTrackMute}
            operationStates={audioActionOperationStates}
            />
          </div>}
          <TimelineShortcutLegend />
        </div>

        {directorActive && <div className="absolute right-4 top-16 z-10 flex items-center gap-3 rounded-lg border border-border bg-surface-1 p-3" role="status" data-testid="timeline-director-status">
          <span className="text-sm text-muted-foreground">{t('timelineDirector.updating')}</span>
          <button type="button" onClick={() => void handleStopDirector()} disabled={stoppingDirector || !directorControl}
            className="h-7 rounded-lg border border-input-border bg-surface-1 px-3 text-sm text-foreground hover:border-input-border-hover hover:bg-surface-3 disabled:opacity-50"
            data-testid="timeline-stop-director">{t(stoppingDirector ? 'timelineEditorModal.cancelling' : 'timelineDirector.stop')}</button>
        </div>}

        {/* Non-blocking render status; tracks and chat remain visible. */}
        {isRenderActive && (
          <div
            className="absolute right-4 top-28 z-10"
            onClick={(e) => e.stopPropagation()}
          >
            <div className="border border-border bg-surface-1 rounded-xl p-3 flex flex-col items-center gap-2 max-w-sm">
              <div className="w-8 h-8 border-3 border-teal-500 border-t-transparent rounded-full animate-spin" />
              <div className="text-sm text-text-secondary text-center">
                {renderMessage || t('timelineEditorModal.renderingInBackground')}
              </div>
              {renderProgress > 0 && (
                <div className="w-full">
                  <div className="h-2 bg-black/30 rounded-full overflow-hidden">
                    <div
                      className="h-full bg-gradient-to-r from-teal-500 to-teal-400 transition-all duration-300 ease-out"
                      style={{ width: `${renderProgress}%` }}
                    />
                  </div>
                </div>
              )}
              <div className="flex items-center gap-3 mt-2">
                <button
                  onClick={handleClose}
                  className="h-9 px-4 rounded-lg bg-primary hover:bg-primary/90 text-white text-sm font-medium transition-colors"
                  data-testid="render-overlay-go-back"
                >
                  {t('timelineEditorModal.goBackToCanvas')}
                </button>
                <button
                  onClick={handleCancelRender}
                  disabled={isCancelling}
                  className="h-9 px-4 rounded-lg border border-destructive/50 bg-destructive/10 hover:bg-destructive/20 text-destructive text-sm font-medium transition-colors disabled:opacity-50"
                  data-testid="render-overlay-cancel"
                >
                  {isCancelling ? t('timelineEditorModal.cancelling') : t('timelineEditorModal.cancel')}
                </button>
              </div>
            </div>
          </div>
        )}

        <ExportConfirmSheet
          isOpen={isExportSheetOpen}
          defaultName={exportSheetDefaultName}
          existingLabels={existingLabels}
          onCancel={handleCancelExportSheet}
          onConfirm={handleConfirmExport}
        />
      </div>
    </div>,
    document.body
  )
}
